/**
 * CanvasStage - the react-konva infinite drawing surface + element engine.
 *
 * The engine lives in TSX (refs, ResizeObserver, Konva event/geometry math);
 * orchestration and all app UI stay in Jac (.cl.jac). Imported by EditorShell.
 *
 * Owns: measured size, camera (pan + wheel-zoom), the element list, draw-to-create,
 * select, drag-move, resize (Transformer), inline text edit, delete, undo/redo,
 * and per-page localStorage persistence.
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import { Stage, Layer, Rect, Ellipse, Line, Text, Arrow, Circle, Image as KonvaImage, Group as KonvaGroup, Transformer, Path } from "react-konva";
import { Sparkles, LayoutTemplate, FileCode2, FileJson, Minus, Plus, Maximize, Map as MapIcon, X, Check, Copy, Trash2 } from "lucide-react";
import { jsPDF } from "jspdf";
import "svg2pdf.js";  // augments jsPDF with .svg() - lets us write vector PDFs, not a raster
import {
  ShapeKind,
  Routing,
  El,
  Comment,
  Pt,
  Side,
  centerOf,
  dist,
  anchor,
  sideAnchor,
  fixedSideAnchor,
  nearestSide,
  isHoriz,
  sideDir,
  orthPoints,
  curvePoints,
  polyMid,
  orthogonalize,
  CONN_GAP,
  segDist,
  norm,
  segsOf,
  properCross,
  hScore,
  withJumps,
  simplifyPath,
  avoidRoute,
  connectorPath,
  INK,
  FILL,
  NOTE,
  storeKeyFor,
  clamp,
  GRID,
  snap,
  snapPt,
  xesc,
  polyStr,
  shapePoly,
  shapePath,
  PATH_KINDS,
  safeColor,
  elsToSvg,
  elsToMermaid,
  seq,
  uid,
  shapeAtPoint,
  BIND_PAD,
  fontStack,
  fontSizeOf,
  touchIntent,
  PEN_GRACE_MS,
} from "./geometry.ts";

// Konva image node that loads its own bitmap from a data URL.
function URLImage({ el }: { el: El }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!el.src) { setImg(null); setFailed(false); return; }
    let live = true;                               // guard setState after unmount / src change
    setFailed(false);
    const i = new window.Image();
    i.onload = () => { if (live) setImg(i); };
    i.onerror = () => { if (live) setFailed(true); };  // corrupt/truncated data: URL → visible error, not a silent empty box
    i.src = el.src;
    return () => { live = false; };
  }, [el.src]);
  if (failed) return <Rect width={el.w} height={el.h} fill="#fee2e2" stroke="#ef4444" strokeWidth={1} dash={[4, 4]} cornerRadius={4} />;
  return <KonvaImage image={img || undefined} width={el.w} height={el.h} cornerRadius={4} />;
}

// Live minimap: element overview + viewport rectangle; click / drag to navigate.
function MiniMap({ els, cam, size, onNav }: { els: El[]; cam: any; size: any; onNav: (x: number, y: number) => void }) {
  const MW = 176, MH = 116, pad = 8;
  const dragging = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const vTL = { x: (0 - cam.x) / cam.scale, y: (0 - cam.y) / cam.scale };
  const vBR = { x: (size.w - cam.x) / cam.scale, y: (size.h - cam.y) / cam.scale };
  let minX = vTL.x, minY = vTL.y, maxX = vBR.x, maxY = vBR.y;
  for (const e of els) { if (e.kind === "connector" || e.hidden) continue; minX = Math.min(minX, e.x); minY = Math.min(minY, e.y); maxX = Math.max(maxX, e.x + e.w); maxY = Math.max(maxY, e.y + e.h); }
  const bw = Math.max(1, maxX - minX), bh = Math.max(1, maxY - minY);
  const s = Math.min((MW - pad * 2) / bw, (MH - pad * 2) / bh);
  const ox = pad - minX * s + ((MW - pad * 2) - bw * s) / 2;
  const oy = pad - minY * s + ((MH - pad * 2) - bh * s) / 2;
  const tx = (x: number) => x * s + ox, ty = (y: number) => y * s + oy;
  const nav = (clientX: number, clientY: number) => {
    const r = svgRef.current?.getBoundingClientRect(); if (!r) return;
    onNav((clientX - r.left - ox) / s, (clientY - r.top - oy) / s);
  };
  return (
    <svg ref={svgRef} width={MW} height={MH}
      onMouseDown={(e) => { dragging.current = true; nav(e.clientX, e.clientY); }}
      onMouseMove={(e) => { if (dragging.current) nav(e.clientX, e.clientY); }}
      onMouseUp={() => { dragging.current = false; }}
      onMouseLeave={() => { dragging.current = false; }}
      className="block cursor-pointer"
    >
      {els.filter((e) => e.kind !== "connector" && !e.hidden).map((e) => {
        // draw shapes (imported freehand/line) → show their real outline, not a box
        if (e.kind === "draw" && e.points && e.points.length > 1) {
          const pd: string[] = []; for (let i = 0; i + 1 < e.points.length; i += 2) pd.push(`${tx(e.x + e.points[i])},${ty(e.y + e.points[i + 1])}`);
          const Tag = e.closed ? "polygon" : "polyline";
          return <Tag key={e.id} points={pd.join(" ")} fill={e.closed && e.fill !== "transparent" ? e.fill : "none"} stroke={e.stroke} strokeWidth={0.5} opacity={e.opacity ?? 1} />;
        }
        if (e.kind === "ellipse") return <ellipse key={e.id} cx={tx(e.x + e.w / 2)} cy={ty(e.y + e.h / 2)} rx={Math.max(0.5, e.w * s / 2)} ry={Math.max(0.5, e.h * s / 2)} fill={e.fill === "transparent" ? "#cbd5e1" : e.fill} stroke={e.stroke} strokeWidth={0.5} />;
        return <rect key={e.id} x={tx(e.x)} y={ty(e.y)} width={Math.max(1, e.w * s)} height={Math.max(1, e.h * s)}
          fill={e.fill === "transparent" ? "#cbd5e1" : e.fill} stroke={e.stroke} strokeWidth={0.5} rx={1} />;
      })}
      <rect x={tx(vTL.x)} y={ty(vTL.y)} width={(vBR.x - vTL.x) * s} height={(vBR.y - vTL.y) * s}
        fill="rgba(79,70,229,0.10)" stroke="#4f46e5" strokeWidth={1.2} rx={1} />
    </svg>
  );
}

interface Props {
  activeTool: string;
  drawKind: string;
  onToolConsumed: () => void;
  onSelect?: (el: any) => void;              // report selection up to the Jac shell
  styleReq?: { n: number; patch: any };      // style edits pushed down from the Properties panel
  selectReq?: { n: number; id: string | null }; // select an element by id (from the Layers panel)
  commandReq?: { n: number; name: string; arg?: any }; // imperative commands from menus / pagebar / palette
  pageId?: string;                           // active page id (the component is keyed by this)
  initialElements?: any;                     // authoritative seed elements for this page (backend)
  onPersist?: (pageId: string, els: any) => void; // debounced save of this page to the backend (SavePage)
  comments?: any[];                          // comment pins for this page (owned by the shell)
  activeCommentId?: string | null;           // highlighted / open comment
  onPlaceComment?: (x: number, y: number) => void; // comment tool dropped a pin at world coords
  onOpenComment?: (id: string) => void;      // a pin was clicked
  insertReq?: { n: number; stencil: any };   // insert a stencil (library shape group) at viewport center
  syncReq?: { n: number; elements: any; pageId?: string }; // authoritative elements pushed in from a live collaborator (for a specific page)
  onLocalChange?: (els: any) => void;        // fast (120ms) hook for broadcasting local edits to peers
  onCursor?: (x: number, y: number) => void; // report local pointer (world coords) for presence
  remoteCursors?: { id: string; name: string; color: string; x: number; y: number }[]; // peers' live pointers
  editable?: boolean;                        // false = read-only (viewer/commenter): no shape create/move/edit
  onImport?: () => void;                      // open the Excalidraw-scene import modal (starter card)
  onTemplate?: () => void;                    // open the template picker (starter card)
  docName?: string;                          // diagram name - used to name exported files
  docId?: string;                            // diagram id - scopes the device mirror per diagram
}

// Tool cursors. The pointer should say what the tool will do, so the pen and eraser carry
// their own glyph instead of a generic arrow. Each is an inline SVG data-URI - no asset to
// ship or fail to load - drawn twice, a white underlay then the dark glyph, so it stays
// visible on both the light and dark canvas. The trailing numbers are the hotspot (the
// pen's nib / the eraser's tip); the keyword after it is the mandatory fallback.
const svgCursor = (body: string, hx: number, hy: number) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<g stroke="#ffffff" stroke-width="4.5">${body}</g><g stroke="#1f2430" stroke-width="2">${body}</g></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${hx} ${hy}, crosshair`;
};
const PEN_CURSOR = svgCursor('<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>', 2, 21);
const ERASER_CURSOR = svgCursor('<path d="m7 21-4.3-4.3a2.4 2.4 0 0 1 0-3.4l9.6-9.6a2.4 2.4 0 0 1 3.4 0l5.6 5.6a2.4 2.4 0 0 1 0 3.4L13 21"/><path d="M22 21H7"/>', 4, 20);
/** The cursor for the active tool - the single source of truth for what the pointer looks like. */
const cursorFor = (tool: string, placing: boolean): string => {
  if (placing) return "crosshair";
  if (tool === "pen") return PEN_CURSOR;
  if (tool === "eraser") return ERASER_CURSOR;
  if (tool === "pan") return "grab";
  if (tool === "shape" || tool === "text" || tool === "sticky" || tool === "frame" || tool === "table" || tool === "comment" || tool === "connector") return "crosshair";
  return "default";
};

export function CanvasStage({ activeTool, drawKind, onToolConsumed, onSelect, styleReq, selectReq, commandReq, pageId, initialElements, onPersist, comments, activeCommentId, onPlaceComment, onOpenComment, insertReq, syncReq, onLocalChange, onCursor, remoteCursors, editable = true, onImport, onTemplate, docName, docId }: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<any>(null);
  const trRef = useRef<any>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [cam, setCam] = useState({ scale: 1, x: 0, y: 0 });
  const [els, setEls] = useState<El[]>([]);
  const [draft, setDraft] = useState<El | null>(null);
  // multi-selection is the source of truth; selId is the single-selection view that all the
  // existing single-element UI (connector handles, Properties, style) reads. setSelId is a shim.
  const [selIds, setSelIds] = useState<string[]>([]);
  const selId = selIds.length === 1 ? selIds[0] : null;
  const setSelId = useCallback((id: string | null) => setSelIds(id ? [id] : []), []);
  const marquee = useRef<{ x0: number; y0: number } | null>(null); // rubber-band origin (world)
  const [marqueeRect, setMarqueeRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const dragBase = useRef<{ id: string; x: number; y: number }[] | null>(null); // group-drag start positions
  const [editId, setEditId] = useState<string | null>(null);
  const [editCell, setEditCell] = useState<number>(-1);   // table cell index being edited (-1 = none)
  const [connectFrom, setConnectFrom] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ x: number; y: number } | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null); // shape highlighted as a connect target
  const connecting = useRef(false);                            // dragging a connection out of a handle
  const bendRef = useRef<number | null>(null);                 // waypoint index being pulled while bending a line
  const connDraw = useRef<{ from: string | null; pt: Pt } | null>(null); // in-progress arrow (press→drag→release, free endpoints ok)
  const [guides, setGuides] = useState<{ pts: number[] }[]>([]); // live alignment guide lines while dragging a shape
  const [ctxMenu, setCtxMenu] = useState<{ sx: number; sy: number; onObject: boolean } | null>(null); // right-click context menu (#30)
  const hoverClear = useRef<any>(null);
  const [showMap, setShowMap] = useState(true);
  const [pendingStencil, setPendingStencil] = useState<any>(null); // a library icon armed for click-to-place (#43)
  const drawing = useRef(false);
  // Freehand ink capture (pen tool). World-space points are collected during the drag and
  // committed as one `draw` element on release; `inkPts` mirrors them for the live preview.
  const ink = useRef<Pt[] | null>(null);
  const [inkPts, setInkPts] = useState<number[] | null>(null);
  const draftRef = useRef<El | null>(null); // source of truth for onMouseUp (state lags on a fast click)
  const start = useRef({ x: 0, y: 0 });
  const past = useRef<El[][]>([]);
  const future = useRef<El[][]>([]);
  const loaded = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // history-aware mutation (create / move / resize / edit / delete go through here)
  const commit = useCallback((updater: (prev: El[]) => El[]) => {
    setEls((prev) => { past.current.push(prev); if (past.current.length > 100) past.current.shift(); future.current = []; return updater(prev); });
  }, []);
  const undo = useCallback(() => setEls((prev) => { const p = past.current.pop(); if (p === undefined) return prev; future.current.push(prev); return p; }), []);
  const redo = useCallback(() => setEls((prev) => { const f = future.current.pop(); if (f === undefined) return prev; past.current.push(prev); return f; }), []);
  // delete an element AND any connectors attached to it (no orphan edges)
  const removeEl = useCallback((id: string) => commit((prev) => prev.filter((p) => p.id !== id && !(p.kind === "connector" && (p.from === id || p.to === id)))), [commit]);

  // move an element by (dx,dy). Shapes shift x/y; connectors shift their free geometry
  // (bound endpoints follow their shapes automatically, so only free points/waypoints move)
  // so a selected arrow travels with the rest of the selection like any other shape.
  const translateEl = (p: El, dx: number, dy: number): El =>
    p.kind === "connector"
      ? { ...p,
          fromPoint: p.fromPoint ? { x: p.fromPoint.x + dx, y: p.fromPoint.y + dy } : p.fromPoint,
          toPoint: p.toPoint ? { x: p.toPoint.x + dx, y: p.toPoint.y + dy } : p.toPoint,
          waypoints: p.waypoints ? p.waypoints.map((w) => ({ x: w.x + dx, y: w.y + dy })) : p.waypoints,
          labelPos: p.labelPos ? { x: p.labelPos.x + dx, y: p.labelPos.y + dy } : p.labelPos }
      : { ...p, x: p.x + dx, y: p.y + dy };

  // topmost shape (non-connector) whose box contains p - later-drawn shapes win. Strict
  // inside-only test, used for selection.
  const shapeAt = (p: Pt): El | undefined => shapeAtPoint(els, p, 0);
  // Like shapeAt but with a small tolerance so a connector endpoint released ON a shape's
  // outline (or a few px outside it) still binds (#61) - aiming at the edge of a box is the
  // natural gesture for connecting to it, and a strict inside-only test made it silently fail.
  const bindShapeAt = (p: Pt): El | undefined => shapeAtPoint(els, p, BIND_PAD);

  // a fresh connector with the clean default look: straight, light stroke, single end arrow
  const makeConnector = (from: string | null, to: string | null): El => ({
    id: uid(), kind: "connector", x: 0, y: 0, w: 0, h: 0,
    stroke: INK, strokeWidth: 2, fill: "transparent",
    from, to, routing: "straight", endArrow: true, startArrow: false, endHead: "arrow", startHead: "none", dashed: false, label: "",
  });

  // gentle alignment snapping: nudge a dragged shape so an edge/center lines up with
  // another shape's edge/center when close, and return the guide lines to draw. No hard grid.
  const alignSnap = (moving: El, x: number, y: number) => {
    const SNAP = 6;
    const others = els.filter((e) => e.id !== moving.id && e.kind !== "connector");
    const xs = [x, x + moving.w / 2, x + moving.w];
    const ys = [y, y + moving.h / 2, y + moving.h];
    let bestX: { d: number; line: number; o: El } | null = null;
    let bestY: { d: number; line: number; o: El } | null = null;
    for (const o of others) {
      const oxs = [o.x, o.x + o.w / 2, o.x + o.w];
      const oys = [o.y, o.y + o.h / 2, o.y + o.h];
      for (const mv of xs) for (const ov of oxs) { const d = ov - mv; if (Math.abs(d) <= SNAP && (!bestX || Math.abs(d) < Math.abs(bestX.d))) bestX = { d, line: ov, o }; }
      for (const mv of ys) for (const ov of oys) { const d = ov - mv; if (Math.abs(d) <= SNAP && (!bestY || Math.abs(d) < Math.abs(bestY.d))) bestY = { d, line: ov, o }; }
    }
    const nx = bestX ? x + bestX.d : x;
    const ny = bestY ? y + bestY.d : y;
    const gs: { pts: number[] }[] = [];
    if (bestX) { const o = bestX.o; gs.push({ pts: [bestX.line, Math.min(ny, o.y), bestX.line, Math.max(ny + moving.h, o.y + o.h)] }); }
    if (bestY) { const o = bestY.o; gs.push({ pts: [Math.min(nx, o.x), bestY.line, Math.max(nx + moving.w, o.x + o.w), bestY.line] }); }
    return { x: nx, y: ny, guides: gs };
  };

  // measure
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Seed this page on mount (the component is keyed by pageId, so it remounts per page):
  // prefer the authoritative backend elements; fall back to the device copy for anonymous use.
  const storeKey = storeKeyFor(pageId, docId);
  useEffect(() => {
    if (loaded.current) return;
    let seed: El[] = Array.isArray(initialElements) && initialElements.length > 0 ? initialElements : [];
    if (seed.length === 0) {
      try { const raw = localStorage.getItem(storeKey); if (raw) seed = JSON.parse(raw); } catch { /* ignore */ }
    }
    if (seed.length > 0) setEls(seed);
    loaded.current = true;
  }, []);
  // Persist (debounced): always to device; onPersist syncs this page to the backend when signed in.
  const elsRef = useRef(els); elsRef.current = els;
  useEffect(() => {
    if (!loaded.current) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(storeKey, JSON.stringify(els)); } catch { /* ignore */ }
      onPersist?.(pageId || "default", els);
    }, 500);
    return () => clearTimeout(t);
  }, [els]);
  // Flush immediately on unmount (e.g. switching page) so no in-flight edits are lost.
  useEffect(() => () => {
    if (!loaded.current) return;
    try { localStorage.setItem(storeKey, JSON.stringify(elsRef.current)); } catch { /* ignore */ }
    onPersist?.(pageId || "default", elsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // React unmount cleanup does NOT run on a real tab close / hard refresh - the JS
  // context is torn down first - so an edit made inside the last debounce window would
  // be lost. A synchronous pagehide/beforeunload flush closes the
  // local-mirror gap (localStorage.setItem is sync and completes before unload).
  useEffect(() => {
    const flush = () => {
      if (!loaded.current) return;
      try { localStorage.setItem(storeKey, JSON.stringify(elsRef.current)); } catch { /* ignore */ }
      onPersist?.(pageId || "default", elsRef.current);
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => { window.removeEventListener("pagehide", flush); window.removeEventListener("beforeunload", flush); };
  }, [pageId, onPersist, storeKey]);

  // --- live collaboration -------------------------------------------------
  // Apply a peer's authoritative element list. `remoteApply` suppresses the very
  // next local-broadcast so an applied remote edit doesn't echo back out.
  const remoteApply = useRef(false);
  const lastCursor = useRef(0);
  const syncN = syncReq?.n ?? 0;
  // Treat the mount-time syncN as already applied. Without this, if the canvas REMOUNTS
  // (which happens on a failed save) this effect re-fired with the STALE collabSync value -
  // usually the page's empty initial snapshot - and setEls([]) WIPED the in-flight drawing.
  // The seed effect above already restores the mounted content from initialElements/device,
  // so only a genuinely NEW sync (a collab edit or an exit-preview/restore re-seed) applies.
  const seenSyncN = useRef(syncN);
  useEffect(() => {
    if (syncN === seenSyncN.current) return;
    seenSyncN.current = syncN;
    // apply only when this update targets the page we're showing (our pageId is
    // always fresh, unlike the shell's WS handler which reads a stale activePageId)
    const forPage = !syncReq?.pageId || syncReq.pageId === pageId;
    if (syncN > 0 && Array.isArray(syncReq?.elements) && forPage) {
      remoteApply.current = true;
      // A peer's edit is now part of ground truth. Drop local undo/redo history so a
      // later ⌘Z can't roll back - and re-broadcast away - the peer's change.
      // Costs the local user their pre-sync history; better than silent loss.
      past.current = [];
      future.current = [];
      setEls(syncReq!.elements as El[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncN]);
  // Broadcast local edits to peers quickly (separate from the 500ms device persist).
  useEffect(() => {
    if (!loaded.current || !onLocalChange) return;
    if (remoteApply.current) { remoteApply.current = false; return; }
    const t = setTimeout(() => onLocalChange(els), 120);
    return () => clearTimeout(t);
  }, [els]);

  // attach transformer to the selected node(s) (select tool only). Connectors are edited via their
  // own handles, so exclude them from the box transformer.
  useEffect(() => {
    const tr = trRef.current, stage = stageRef.current;
    if (!tr || !stage) return;
    const ids = activeTool === "select" && !editId && editable
      ? selIds.filter((id) => els.find((e) => e.id === id && e.kind !== "connector" && !e.locked))
      : [];
    const nodes = ids.map((id) => stage.findOne("#" + id)).filter(Boolean);
    tr.nodes(nodes as any);
    tr.getLayer()?.batchDraw();
  }, [selIds, activeTool, editId, els, size, editable]);

  // report selection up to the Jac Properties panel (single selection only)
  useEffect(() => {
    if (selIds.length === 0) { onSelect?.(null); return; }
    if (selIds.length === 1) {
      const one = els.find((e) => e.id === selIds[0]) ?? null;
      // always report a defined opacity so the Properties slider has a position
      onSelect?.(one ? { ...one, opacity: one.opacity ?? 1, _ids: [one.id] } : null);
      return;
    }
    // multi-selection: report a synthetic element carrying the SHARED style values
    // (null where the selection disagrees, so no swatch falsely reads as active).
    const sel = els.filter((e) => selIds.includes(e.id));
    const shared = (k: string) => {
      const v0 = (sel[0] as any)[k];
      return sel.every((e) => (e as any)[k] === v0) ? v0 : null;
    };
    onSelect?.({
      kind: "multiple",
      _multi: sel.length,
      _ids: selIds,
      id: null,
      fill: shared("fill"),
      stroke: shared("stroke"),
      strokeWidth: shared("strokeWidth"),
      opacity: shared("opacity") ?? (sel[0]?.opacity ?? 1),
    } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selIds, els]);

  // select an element by id, driven from the Layers panel (counter-triggered)
  const selN = selectReq?.n ?? 0;
  useEffect(() => {
    if (selN > 0) setSelId(selectReq?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selN]);

  // apply a style patch pushed down from the Properties panel (counter-triggered)
  const styleN = styleReq?.n ?? 0;
  useEffect(() => {
    if (styleN > 0 && selIds.length && styleReq?.patch && editable) {
      const set = new Set(selIds);
      commit((prev) => prev.map((p) => (set.has(p.id) ? { ...p, ...styleReq.patch } : p)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleN]);

  // duplicate every selected (non-connector) element, keeping the copies selected
  const duplicateSelected = () => {
    const srcs = els.filter((e) => selIds.includes(e.id) && e.kind !== "connector");
    if (!srcs.length) return;
    // copied group members form a NEW group (one fresh id per source group)
    const remap: Record<string, string> = {};
    const copies = srcs.map((s) => {
      let gid = s.groupId;
      if (gid) { if (!remap[gid]) remap[gid] = "g_" + uid(); gid = remap[gid]; }
      return { ...s, id: uid(), x: s.x + 16, y: s.y + 16, groupId: gid };
    });
    commit((prev) => [...prev, ...copies]);
    setSelIds(copies.map((c) => c.id));
  };
  // remove every selected element (+ connectors attached to any of them)
  const removeSelected = () => {
    if (!selIds.length) return;
    const set = new Set(selIds);
    commit((prev) => prev.filter((p) => !set.has(p.id) && !(p.kind === "connector" && ((p.from && set.has(p.from)) || (p.to && set.has(p.to))))));
    setSelIds([]);
  };
  // --- align / distribute / match size (#21) ----------------------------
  // Operates on the multi-selection as ONE undo step. Align + distribute are group-aware:
  // shapes sharing a groupId move together as one unit (so a group aligns as one object).
  // Match resizes each selected shape to the largest width/height in the selection.
  // Connectors carry no independent position, so they are excluded and re-route from their
  // endpoints automatically after the shapes move.
  const arrange = (op: string) => {
    if (!editable) return;
    const shapes = els.filter((e) => selIds.includes(e.id) && e.kind !== "connector");
    if (shapes.length < 2) return;
    if (op === "matchw" || op === "matchh" || op === "matchboth") {
      const maxW = Math.max(...shapes.map((s) => s.w));
      const maxH = Math.max(...shapes.map((s) => s.h));
      const set = new Set(shapes.map((s) => s.id));
      commit((prev) => prev.map((p) => set.has(p.id)
        ? { ...p, w: (op === "matchh" ? p.w : maxW), h: (op === "matchw" ? p.h : maxH) }
        : p));
      return;
    }
    // build group-aware units (a group = one movable unit sharing a bounding box)
    const byUnit = new Map<string, El[]>();
    for (const e of shapes) { const k = e.groupId || e.id; const arr = byUnit.get(k); if (arr) arr.push(e); else byUnit.set(k, [e]); }
    const units = [...byUnit.values()].map((members) => {
      const minX = Math.min(...members.map((m) => m.x)), minY = Math.min(...members.map((m) => m.y));
      const maxX = Math.max(...members.map((m) => m.x + m.w)), maxY = Math.max(...members.map((m) => m.y + m.h));
      return { members, minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
    });
    const delta = new Map<string, { dx: number; dy: number }>();
    const move = (u: (typeof units)[number], dx: number, dy: number) => { for (const m of u.members) delta.set(m.id, { dx, dy }); };
    if (op === "left") { const t = Math.min(...units.map((u) => u.minX)); units.forEach((u) => move(u, t - u.minX, 0)); }
    else if (op === "right") { const t = Math.max(...units.map((u) => u.maxX)); units.forEach((u) => move(u, t - u.maxX, 0)); }
    else if (op === "hcenter") { const t = (Math.min(...units.map((u) => u.minX)) + Math.max(...units.map((u) => u.maxX))) / 2; units.forEach((u) => move(u, t - u.cx, 0)); }
    else if (op === "top") { const t = Math.min(...units.map((u) => u.minY)); units.forEach((u) => move(u, 0, t - u.minY)); }
    else if (op === "bottom") { const t = Math.max(...units.map((u) => u.maxY)); units.forEach((u) => move(u, 0, t - u.maxY)); }
    else if (op === "vmiddle") { const t = (Math.min(...units.map((u) => u.minY)) + Math.max(...units.map((u) => u.maxY))) / 2; units.forEach((u) => move(u, 0, t - u.cy)); }
    else if (op === "disth" || op === "distv") {
      if (units.length < 3) return;
      const horiz = op === "disth";
      const sorted = [...units].sort((a, b) => (horiz ? a.cx - b.cx : a.cy - b.cy));
      const totalSize = sorted.reduce((s, u) => s + (horiz ? u.w : u.h), 0);
      const span = horiz ? sorted[sorted.length - 1].maxX - sorted[0].minX : sorted[sorted.length - 1].maxY - sorted[0].minY;
      const gap = (span - totalSize) / (sorted.length - 1);
      let cursor = horiz ? sorted[0].minX : sorted[0].minY;
      for (const u of sorted) { if (horiz) { move(u, cursor - u.minX, 0); cursor += u.w + gap; } else { move(u, 0, cursor - u.minY); cursor += u.h + gap; } }
    } else { return; }
    if (delta.size) commit((prev) => prev.map((p) => { const d = delta.get(p.id); return d ? { ...p, x: p.x + d.dx, y: p.y + d.dy } : p; }));
  };
  // --- z-order / lock / hide (#29) --------------------------------------
  // Render order = array order (later = on top). Reordering the selected elements as one
  // contiguous block, preserving the order of the rest, is one undo step and persists via
  // SavePage (element order). Locked/hidden are generic fields that flow through unchanged.
  const reorderZ = (op: string) => {
    if (!editable || !selIds.length) return;
    const set = new Set(selIds);
    commit((prev) => {
      const sel = prev.filter((e) => set.has(e.id));
      const rest = prev.filter((e) => !set.has(e.id));
      if (!sel.length) return prev;
      if (op === "toFront") return [...rest, ...sel];
      if (op === "toBack") return [...sel, ...rest];
      // forward/backward: shift the block one slot among the non-selected elements
      const minIdx = prev.findIndex((e) => set.has(e.id));
      const restBefore = prev.slice(0, minIdx).filter((e) => !set.has(e.id)).length;
      let at = restBefore + (op === "forward" ? 1 : -1);
      at = Math.max(0, Math.min(rest.length, at));
      return [...rest.slice(0, at), ...sel, ...rest.slice(at)];
    });
  };
  // Toggle lock/hide on `id` (Layers panel) or the whole selection. If any target is
  // currently unlocked/visible, the toggle locks/hides ALL targets (and deselects them, so
  // a locked/hidden object isn't left with active transform handles); else it reverses.
  const toggleLock = (id?: string) => {
    const ids = id ? [id] : selIds;
    if (!editable || !ids.length) return;
    const set = new Set(ids);
    const lock = els.some((e) => set.has(e.id) && !e.locked);
    commit((prev) => prev.map((p) => (set.has(p.id) ? { ...p, locked: lock } : p)));
    if (lock) setSelIds((s) => s.filter((x) => !set.has(x)));
  };
  const toggleHide = (id?: string) => {
    const ids = id ? [id] : selIds;
    if (!editable || !ids.length) return;
    const set = new Set(ids);
    const hide = els.some((e) => set.has(e.id) && !e.hidden);
    commit((prev) => prev.map((p) => (set.has(p.id) ? { ...p, hidden: hide } : p)));
    if (hide) setSelIds((s) => s.filter((x) => !set.has(x)));
  };
  // --- right-click context menu (#30) -----------------------------------
  // Right-clicking selects the object under the cursor (group-aware) if it isn't already in
  // the selection, then opens a LibreDraw menu with the actions applicable to that selection
  // (or the canvas-level actions on empty space). Suppresses the browser's default menu.
  const openContextMenu = (e: any) => {
    if (!editable) return;
    e.preventDefault();
    const box = boxRef.current; if (!box) return;
    const rect = box.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const wp = { x: (sx - cam.x) / cam.scale, y: (sy - cam.y) / cam.scale };
    const hit = [...els].reverse().find((el) => el.kind !== "connector" && !el.hidden && !el.locked
      && wp.x >= el.x && wp.x <= el.x + el.w && wp.y >= el.y && wp.y <= el.y + el.h);
    if (hit) {
      if (!selIds.includes(hit.id)) setSelIds(groupMemberIds(hit.id));
      setCtxMenu({ sx, sy, onObject: true });
    } else {
      setCtxMenu({ sx, sy, onObject: false });
    }
  };
  const focusCanvas = () => { const b: any = boxRef.current; b?.focus?.(); };
  const runCtx = (fn: () => void) => { fn(); setCtxMenu(null); focusCanvas(); };
  const ctxItem = (label: string, shortcut: string, danger: boolean, onClick: () => void) => (
    <button role="menuitem" onClick={onClick}
      className={`flex w-full items-center justify-between gap-6 px-3 py-1.5 text-left text-sm hover:bg-accent ${danger ? "text-destructive hover:bg-destructive/10" : "text-foreground"}`}>
      <span>{label}</span>{shortcut ? <span className="text-xs text-muted-foreground">{shortcut}</span> : null}
    </button>
  );
  // --- grouping ---------------------------------------------------------
  const groupSelected = (name?: string) => {
    const ids = selIds.filter((id) => els.find((e) => e.id === id));
    if (ids.length < 2 && !name) return;   // need ≥2 to group by hand; a library insert passes a name
    const set = new Set(ids);
    const gid = "g_" + uid();
    const existing = els.find((e) => set.has(e.id) && e.groupName);
    const gname = name || existing?.groupName || "Group";
    commit((prev) => prev.map((p) => (set.has(p.id) ? { ...p, groupId: gid, groupName: gname } : p)));
  };
  const ungroupSelected = () => {
    if (!selIds.length) return;
    const set = new Set(selIds);
    commit((prev) => prev.map((p) => (set.has(p.id) ? { ...p, groupId: undefined, groupName: undefined } : p)));
  };
  const selectGroup = (gid: string) => setSelIds(els.filter((e) => e.groupId === gid).map((e) => e.id));
  const renameGroup = (gid: string, name: string) => commit((prev) => prev.map((p) => (p.groupId === gid ? { ...p, groupName: name } : p)));
  // every element in the same group as `id` (for group-aware click selection)
  const groupMemberIds = (id: string): string[] => {
    const el = els.find((e) => e.id === id);
    if (!el?.groupId) return [id];
    return els.filter((e) => e.groupId === el.groupId).map((e) => e.id);
  };
  // Non-frame elements whose center sits inside a frame - they travel with it when it moves.
  const framedChildren = (f: El): string[] =>
    els.filter((e) => e.id !== f.id && e.kind !== "connector" && e.kind !== "frame"
      && e.x + e.w / 2 >= f.x && e.x + e.w / 2 <= f.x + f.w
      && e.y + e.h / 2 >= f.y && e.y + e.h / 2 <= f.y + f.h).map((e) => e.id);

  // Add/remove a table's rows or columns. Existing cell text stays in place; cell size is
  // held constant so the whole table grows/shrinks (min 1×1). Cells are re-laid row-major.
  const changeTable = (id: string, dRows: number, dCols: number) => commit((prev) => prev.map((p) => {
    if (p.id !== id || p.kind !== "table") return p;
    const rows = Math.max(1, p.rows || 3), cols = Math.max(1, p.cols || 3);
    const nrows = Math.max(1, rows + dRows), ncols = Math.max(1, cols + dCols);
    const cw = p.w / cols, ch = p.h / rows;
    const old = p.cells || [];
    const cells: string[] = [];
    for (let r = 0; r < nrows; r++) for (let c = 0; c < ncols; c++) cells.push(r < rows && c < cols ? (old[r * cols + c] || "") : "");
    return { ...p, rows: nrows, cols: ncols, cells, w: cw * ncols, h: ch * nrows };
  }));

  // Copy/paste element styles (Excalidraw parity). Alt+Cmd/Ctrl+C captures the selected
  // element's visual style; Alt+Cmd/Ctrl+V applies it to the current selection. Only the
  // style-relevant keys travel (never position/size/text/points).
  const copiedStyle = useRef<any>(null);
  const STYLE_KEYS = ["fill", "stroke", "strokeWidth", "opacity", "dashed", "dotted", "routing", "startHead", "endHead", "fontSize", "fontFamily"];

  // keyboard: undo/redo, select-all, duplicate, delete, nudge, escape, copy/paste-styles
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      const mod = e.metaKey || e.ctrlKey;
      // Style copy/paste - matched on e.code so macOS Option-compose (Option+C -> "ç")
      // can't hide the shortcut; checked before the plain-mod shortcuts below.
      if (mod && e.altKey && e.code === "KeyC") {
        const src = els.find((x) => x.id === selIds[0]);
        if (!src) return;
        e.preventDefault();
        const o: any = {};
        for (const k of STYLE_KEYS) if ((src as any)[k] !== undefined) o[k] = (src as any)[k];
        copiedStyle.current = o;
      }
      else if (mod && e.altKey && e.code === "KeyV") {
        if (!editable || !copiedStyle.current || !selIds.length) return;
        e.preventDefault();
        const set = new Set(selIds);
        const patch = copiedStyle.current;
        commit((prev) => prev.map((p) => (set.has(p.id) ? { ...p, ...patch } : p)));
      }
      else if (mod && e.key.toLowerCase() === "z") { if (!editable) return; e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if (mod && e.key.toLowerCase() === "a") { e.preventDefault(); setSelIds(els.map((x) => x.id)); }
      else if (mod && e.key.toLowerCase() === "d") { if (!editable) return; e.preventDefault(); duplicateSelected(); }
      else if (mod && e.key.toLowerCase() === "g") { if (!editable) return; e.preventDefault(); e.shiftKey ? ungroupSelected() : groupSelected(); }
      // z-order: ⌘] to front, ⌘[ to back, ] forward, [ backward
      else if (e.key === "]" && selIds.length) { if (!editable) return; e.preventDefault(); reorderZ(mod ? "toFront" : "forward"); }
      else if (e.key === "[" && selIds.length) { if (!editable) return; e.preventDefault(); reorderZ(mod ? "toBack" : "backward"); }
      else if ((e.key === "Delete" || e.key === "Backspace") && selIds.length) { if (!editable) return; e.preventDefault(); removeSelected(); }
      else if (e.key === "Escape") { if (pendingStencil) { setPendingStencil(null); } else if (ctxMenu) { setCtxMenu(null); focusCanvas(); } else { setSelIds([]); setEditId(null); } }
      else if (e.key.startsWith("Arrow") && selIds.length && !mod) {
        if (!editable) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        const set = new Set(selIds);
        commit((prev) => prev.map((p) => (set.has(p.id) ? translateEl(p, dx, dy) : p)));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selIds, els, undo, redo, commit, removeEl, duplicateSelected, groupSelected, ungroupSelected, editable, ctxMenu, pendingStencil]);

  // box shapes that can carry a centered double-click-to-edit text label (Excalidraw parity).
  // Excludes text/sticky (own top-left editors), table (per-cell), and image/draw/frame/connector.
  const LABEL_KINDS = new Set(["rectangle", "ellipse", "diamond", "triangle", "hexagon", "parallelogram", "cylinder"]);
  const canLabel = (el: El) => LABEL_KINDS.has(el.kind);

  const isDrawTool = activeTool === "shape" || activeTool === "text" || activeTool === "sticky" || activeTool === "frame" || activeTool === "table";
  const kindForTool = (): ShapeKind => activeTool === "text" ? "text" : activeTool === "sticky" ? "sticky" : activeTool === "frame" ? "frame" : activeTool === "table" ? "table" : ((drawKind as ShapeKind) || "rectangle");

  const onWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    const p = e.target.getStage()?.getPointerPosition();
    if (!p) return;
    setCam((c) => {
      const old = c.scale;
      const wx = (p.x - c.x) / old, wy = (p.y - c.y) / old;
      const next = clamp(e.evt.deltaY < 0 ? old * 1.06 : old / 1.06, 0.2, 4);
      return { scale: next, x: p.x - wx * next, y: p.y - wy * next };
    });
  }, []);

  // Touch/pointer (Excalidraw parity): a single finger reuses the mouse handlers
  // (draw / select / pan); two fingers pinch-zoom around the pinch midpoint. The camera
  // lives on the Stage, so the mouse handlers' getRelativePointerPosition works for touch too.
  const pinch = useRef<{ dist: number; scale: number; camX: number; camY: number; mx: number; my: number } | null>(null);
  const touchDist = (t: any) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  // ── Stylus & palm handling (iPad) ────────────────────────────────────────────────
  // iOS Safari tags every Touch with touchType ("stylus" for Apple Pencil, "direct" for a
  // finger); pointer events carry the same fact as pointerType. We remember when a stylus was
  // last seen so a palm landing a moment later - which is a plain finger contact - is rejected
  // instead of drawing, selecting or starting a pinch. The stroke position is also taken from
  // the stylus contact rather than whichever contact the browser reported first, so ink follows
  // the pencil even while a hand rests on the glass.
  // One source of truth for the pointer. Hover handlers restore THIS instead of hardcoding
  // "default", which used to strand the canvas on an arrow after brushing any element,
  // and the effect re-applies it on a tool change even if an imperative write got there first.
  const toolCursor = cursorFor(activeTool, !!pendingStencil);
  const cursorRef = useRef(toolCursor);
  cursorRef.current = toolCursor;
  useEffect(() => { const el = stageRef.current?.container?.(); if (el) el.style.cursor = toolCursor; }, [toolCursor]);
  const penSeenAt = useRef(0);
  const penRecent = () => Date.now() - penSeenAt.current < PEN_GRACE_MS;
  const stylusOf = (evt: any) => {
    const list = evt?.touches?.length ? evt.touches : evt?.changedTouches;
    if (!list) return null;
    for (let i = 0; i < list.length; i++) if (list[i].touchType === "stylus") return list[i];
    return null;
  };
  // Palm rejection. Konva listens to POINTER events (pointerEventsEnabled), so that is the only
  // stream worth gating - and gating it is what keeps a palm from reaching node handlers or the
  // drag machinery. Two rules learned the hard way:
  //
  //  1. Never block touch events. iOS derives its pointer events from the same gesture, so
  //     cancelling a palm's touch can silently kill the PEN's pointer stream - which showed up
  //     as strokes that worked, then didn't, then did. Touch events are only read here, to learn
  //     that a contact is a stylus (iOS marks that on the Touch, not on the PointerEvent).
  //  2. Decide once at pointerdown and apply that decision for the pointer's whole life.
  //     Blocking a move or an up for a pointer whose down we let through strands Konva
  //     mid-gesture, and the next stroke lands on a stage that still thinks a pointer is down.
  const blockedPointers = useRef(new Set());
  useEffect(() => {
    if (marquee.current) { marquee.current = null; setMarqueeRect(null); }
  }, [activeTool]);
  useEffect(() => {
    const host = stageRef.current?.container?.();
    if (!host) return;
    const note = (ev) => { if (stylusOf(ev)) penSeenAt.current = Date.now(); };
    const gate = (ev) => {
      if (ev.pointerType === "pen") { penSeenAt.current = Date.now(); return; }
      if (ev.pointerType === "mouse") return;               // a mouse is never a palm
      if (!host.contains(ev.target)) return;                // only guard the canvas
      const id = ev.pointerId;
      if (ev.type === "pointerdown") {
        if (!penRecent()) return;                           // no stylus in play: ordinary touch
        blockedPointers.current.add(id);
      }
      if (!blockedPointers.current.has(id)) return;
      if (ev.type === "pointerup" || ev.type === "pointercancel") {
        blockedPointers.current.delete(id);
        if (marquee.current) { marquee.current = null; setMarqueeRect(null); }
      }
      // stopPropagation alone hides it from Konva; the stage already sets touch-action:none,
      // so there is no browser scroll to preventDefault away.
      ev.stopPropagation();
    };
    const touches = ["touchstart", "touchmove", "touchend"];
    const pointers = ["pointerdown", "pointermove", "pointerup", "pointercancel"];
    touches.forEach((t) => document.addEventListener(t, note, { capture: true, passive: true }));
    pointers.forEach((t) => document.addEventListener(t, gate, { capture: true }));
    return () => {
      touches.forEach((t) => document.removeEventListener(t, note, { capture: true }));
      pointers.forEach((t) => document.removeEventListener(t, gate, { capture: true }));
    };
  }, []);
  // Position of the active contact, preferring the stylus when one is down.
  const relPos = (stage: any, evt?: any) => {
    const st = stylusOf(evt);
    if (st && stage) {
      const box = stage.container().getBoundingClientRect();
      return stage.getAbsoluteTransform().copy().invert().point({ x: st.clientX - box.left, y: st.clientY - box.top });
    }
    return stage.getRelativePointerPosition();
  };
  // Classify a touch through the shared policy, recording stylus use as a side effect.
  const intentOf = (evt: any): "draw" | "pinch" | "ignore" => {
    const pen = evt?.pointerType === "pen" || !!stylusOf(evt);
    if (pen) penSeenAt.current = Date.now();
    return touchIntent({ stylus: pen, touches: evt?.touches?.length || 0, penRecent: penRecent() });
  };

  const onTouchStart = (e: any) => {
    const t = e.evt.touches || [];
    const intent = intentOf(e.evt);
    if (intent === "ignore") { e.evt.preventDefault?.(); return; }
    if (intent === "pinch" && t.length >= 2) {
      const box = e.target.getStage()?.container().getBoundingClientRect();
      pinch.current = { dist: touchDist(t), scale: cam.scale, camX: cam.x, camY: cam.y,
        mx: (t[0].clientX + t[1].clientX) / 2 - (box?.left || 0), my: (t[0].clientY + t[1].clientY) / 2 - (box?.top || 0) };
      return;
    }
    pinch.current = null;
    onMouseDown(e);
  };
  const onTouchMove = (e: any) => {
    const t = e.evt.touches || [];
    // a palm dragging across the glass while the pencil writes must change nothing
    if (intentOf(e.evt) === "ignore") { e.evt.preventDefault?.(); return; }
    if (t.length >= 2 && pinch.current) {
      e.evt.preventDefault();
      const pc = pinch.current;
      const next = clamp(pc.scale * (touchDist(t) / pc.dist), 0.2, 4);
      const wx = (pc.mx - pc.camX) / pc.scale, wy = (pc.my - pc.camY) / pc.scale;
      setCam(() => ({ scale: next, x: pc.mx - wx * next, y: pc.my - wy * next }));
      return;
    }
    onMouseMove(e);
  };
  const onTouchEnd = (e?: any) => {
    // a palm lifting must not end the stroke the pencil is still drawing
    if (e && !stylusOf(e.evt) && penRecent() && (e.evt?.touches?.length || 0) > 0) return;
    pinch.current = null;
    onMouseUp();
  };

  // Eraser: delete the topmost shape under the pointer, else a connector whose line the
  // pointer is on. Works on click and on drag (erasing.current). Deleting a shape also
  // strips its connectors (removeEl). An Excalidraw-parity tool.
  const erasing = useRef(false);
  const eraseAt = (stage: any) => {
    const p = stage.getRelativePointerPosition();
    // A freehand stroke is erased by touching the INK, not its bounding box: a diagonal
    // stroke's box is mostly empty canvas, so a box hit would rub it out from far away.
    for (let i = els.length - 1; i >= 0; i--) {
      const d = els[i];
      if (d.kind !== "draw" || !d.points || d.points.length < 4) continue;
      for (let k = 0; k + 3 < d.points.length; k += 2) {
        const a = { x: d.x + d.points[k], y: d.y + d.points[k + 1] };
        const b = { x: d.x + d.points[k + 2], y: d.y + d.points[k + 3] };
        if (segDist(p, a, b) < 8) { removeEl(d.id); return; }
      }
    }
    const s = shapeAt({ x: p.x, y: p.y });
    if (s && s.kind !== "draw") { removeEl(s.id); return; }
    for (let i = els.length - 1; i >= 0; i--) {
      const c = els[i];
      if (c.kind !== "connector") continue;
      const path = connectorPath(c, els);
      if (!path) continue;
      for (let k = 0; k + 1 < path.pts.length; k++) {
        if (segDist(p, path.pts[k], path.pts[k + 1]) < 8) { removeEl(c.id); return; }
      }
    }
  };

  const onMouseDown = (e: any) => {
    const stage = e.target.getStage();
    if (!stage) return;
    // click-to-place an armed library icon: drop it under the pointer, then disarm (#43)
    if (pendingStencil && editable) {
      const p = stage.getRelativePointerPosition();
      if (p) placeStencil(pendingStencil, p.x, p.y);
      setPendingStencil(null);
      return;
    }
    const onEmpty = e.target === stage;
    if (activeTool === "comment") { if (onEmpty) { const p = stage.getRelativePointerPosition(); onPlaceComment?.(p.x, p.y); } return; }
    if (activeTool === "eraser" && editable) { erasing.current = true; eraseAt(stage); return; }
    // pen: press → drag → release lays down one freehand stroke. Works with a mouse, a finger
    // or a stylus: single-touch is routed here by onTouchStart and the stage sets touchAction
    // "none", so the gesture is never stolen by scrolling.
    if (activeTool === "pen" && editable) {
      const p = relPos(stage, e.evt);
      ink.current = [{ x: p.x, y: p.y }];
      setInkPts([p.x, p.y]);
      return;
    }
    // arrow/connector: press anywhere → drag → release. Endpoints bind to a shape if they
    // land on one, otherwise they're free-floating points (Excalidraw-style).
    if (activeTool === "connector" && editable) {
      const p = stage.getRelativePointerPosition();
      const s = bindShapeAt({ x: p.x, y: p.y });
      connDraw.current = { from: s ? s.id : null, pt: { x: p.x, y: p.y } };
      setConnectFrom(s ? s.id : null);
      setPreview({ x: p.x, y: p.y });
      return;
    }
    if (isDrawTool && onEmpty && editable) {
      const p = stage.getRelativePointerPosition();
      drawing.current = true;
      start.current = { x: p.x, y: p.y };
      const k = kindForTool();
      setSelId(null);
      const nd: El = { id: uid(), kind: k, x: p.x, y: p.y, w: 0, h: 0, stroke: (k === "frame" || k === "table") ? "#94a3b8" : INK, strokeWidth: k === "sticky" ? 1 : (k === "frame" || k === "table") ? 1.5 : 2, fill: k === "sticky" ? NOTE : (k === "text" || k === "frame") ? "transparent" : k === "table" ? "#ffffff" : FILL, text: k === "text" ? "Text" : k === "sticky" ? "Note" : k === "frame" ? "Frame" : undefined, rows: k === "table" ? 3 : undefined, cols: k === "table" ? 3 : undefined, cells: k === "table" ? [] : undefined };
      draftRef.current = nd;
      setDraft(nd);
    } else if (activeTool === "select" && onEmpty && !penRecent()) {
      // begin a rubber-band marquee (and clear selection unless extending with Shift)
      const p = stage.getRelativePointerPosition();
      marquee.current = { x0: p.x, y0: p.y };
      if (!e.evt?.shiftKey) setSelIds([]);
    }
  };

  const onMouseMove = (e: any) => {
    if (onCursor) {
      const now = performance.now();
      if (now - lastCursor.current > 45) {
        lastCursor.current = now;
        const wp = e.target.getStage()?.getRelativePointerPosition();
        if (wp) onCursor(wp.x, wp.y);
      }
    }
    if (activeTool === "eraser" && erasing.current) { const st = e.target.getStage(); if (st) eraseAt(st); return; }
    if (ink.current) {
      const st = e.target.getStage(); if (!st) return;
      const p = relPos(st, e.evt);
      const last = ink.current[ink.current.length - 1];
      // thin the trail: keep points ~2 screen px apart so a slow drag cannot bloat the
      // stroke (and the saved document) with thousands of near-identical samples.
      if (Math.hypot(p.x - last.x, p.y - last.y) * cam.scale < 2) return;
      ink.current.push({ x: p.x, y: p.y });
      setInkPts(ink.current.flatMap((q) => [q.x, q.y]));
      return;
    }
    // dragging out an arrow - from the connector tool OR from a hover port in select mode
    if (connDraw.current) {
      const pp = e.target.getStage()!.getRelativePointerPosition();
      setPreview(pp);
      // highlight the shape the endpoint would bind to (incl. the padded edge zone) so the
      // user can see, before releasing, whether the connector will actually attach (#61)
      const tgt = bindShapeAt(pp);
      setHoverId(tgt ? tgt.id : null);
      return;
    }
    if (marquee.current) {
      const p = e.target.getStage()!.getRelativePointerPosition();
      const m = marquee.current;
      setMarqueeRect({ x: Math.min(m.x0, p.x), y: Math.min(m.y0, p.y), w: Math.abs(p.x - m.x0), h: Math.abs(p.y - m.y0) });
      return;
    }
    if (!drawing.current || !draftRef.current) return;
    const p = e.target.getStage()!.getRelativePointerPosition();
    const nd: El = { ...draftRef.current, x: Math.min(start.current.x, p.x), y: Math.min(start.current.y, p.y), w: Math.abs(p.x - start.current.x), h: Math.abs(p.y - start.current.y) };
    draftRef.current = nd;
    setDraft(nd);
  };

  const onMouseUp = () => {
    connecting.current = false;
    erasing.current = false;
    // finish a freehand stroke: store the path relative to its own bounding box so the
    // element moves/scales like every other one, and reuse the existing `draw` kind (already
    // rendered, exported, erased and persisted) rather than inventing a parallel model.
    if (ink.current) {
      const pts = ink.current;
      ink.current = null; setInkPts(null);
      if (pts.length >= 2) {
        const xs = pts.map((q) => q.x), ys = pts.map((q) => q.y);
        const minX = Math.min(...xs), minY = Math.min(...ys);
        const nd: El = {
          id: uid(), kind: "draw", x: minX, y: minY,
          w: Math.max(1, Math.max(...xs) - minX), h: Math.max(1, Math.max(...ys) - minY),
          points: pts.flatMap((q) => [q.x - minX, q.y - minY]),
          stroke: INK, strokeWidth: 2, fill: "transparent", smooth: true, closed: false,
        };
        commit((prev) => [...prev, nd]);
        // The pen STAYS active: ink is inherently repeated - a written word is a stroke per
        // letter - so reverting to Select after each one would make writing unusable. Like
        // the eraser, it is a continuous tool; Escape / V / another tool leaves it. (The
        // one-shot tools keep the opposite default, with the rail lock for repeat placing.)
        // No auto-select either: a selection box flashing around the last stroke while you
        // write the next one is noise.
      }
      return;
    }
    // finish a rubber-band marquee → select every element intersecting the box
    if (marquee.current) {
      const r = marqueeRect;
      marquee.current = null; setMarqueeRect(null);
      if (r && (r.w > 3 || r.h > 3)) {
        // An object is selected ONLY when its whole bounding box is inside the marquee
        // (full containment). Partially-covered objects are NOT selected. This is one rule
        // for every kind - a shape by its box, an arrow by its routed-path box.
        const contained = (x0: number, y0: number, x1: number, y1: number) =>
          x0 >= r.x && y0 >= r.y && x1 <= r.x + r.w && y1 <= r.y + r.h;
        const hit = els.filter((e) => {
          if (e.locked || e.hidden) return false;   // locked/hidden objects aren't marquee-selectable (#29)
          if (e.kind === "connector") {
            const path = connectorPath(e, els);
            if (!path || !path.pts.length) return false;
            const xs = path.pts.map((p) => p.x), ys = path.pts.map((p) => p.y);
            return contained(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys));
          }
          return contained(e.x, e.y, e.x + e.w, e.y + e.h);
        }).map((e) => e.id);
        setSelIds((s) => Array.from(new Set([...s, ...hit])));
      }
      return;
    }
    // finish an arrow: bind each end to whatever shape it landed on, else keep it free.
    // Runs for the connector tool AND for a drag started from a hover port in select mode.
    if (connDraw.current || activeTool === "connector") {
      const startDraw = connDraw.current;
      connDraw.current = null;
      setConnectFrom(null); setPreview(null); setHoverId(null);
      if (startDraw) {
        const stage = stageRef.current;
        const p = stage ? stage.getRelativePointerPosition() : startDraw.pt;
        const endShape = bindShapeAt({ x: p.x, y: p.y });
        const from = startDraw.from;
        const to = endShape ? endShape.id : null;
        const moved = Math.hypot(p.x - startDraw.pt.x, p.y - startDraw.pt.y) > 6;
        const sameShape = from && to && from === to;
        // create when the user actually drew a segment (or linked two distinct shapes)
        if (!sameShape && (moved || (from && to))) {
          const nc = makeConnector(from, to);
          if (!from) nc.fromPoint = { x: startDraw.pt.x, y: startDraw.pt.y };
          if (!to) nc.toPoint = { x: p.x, y: p.y };
          commit((prev) => [...prev, nc]);
          setSelId(nc.id); onToolConsumed();
        }
      }
      return;
    }
    if (!drawing.current || !draftRef.current) return;
    drawing.current = false;
    let el = draftRef.current;
    // a click (no drag) → give the shape a sensible default size around the point
    if (el.w < 6 && el.h < 6) {
      const dw = el.kind === "text" ? 120 : el.kind === "sticky" ? 170 : 140;
      const dh = el.kind === "text" ? 34 : el.kind === "sticky" ? 120 : 80;
      el = { ...el, w: dw, h: dh };
    }
    commit((prev) => [...prev, el]);
    draftRef.current = null;
    setDraft(null);
    setSelId(el.id);
    // text/sticky: drop straight into edit so you can just type
    if (el.kind === "text" || el.kind === "sticky") setEditId(el.id);
    onToolConsumed();
  };

  const zoomAroundCenter = (factor: number) => setCam((c) => { const next = clamp(c.scale * factor, 0.2, 4); const cx = size.w / 2, cy = size.h / 2; const wx = (cx - c.x) / c.scale, wy = (cy - c.y) / c.scale; return { scale: next, x: cx - wx * next, y: cy - wy * next }; });
  const fit = () => {
    if (els.length === 0) { setCam({ scale: 1, x: 0, y: 0 }); return; }
    const minX = Math.min(...els.map((e) => e.x)), minY = Math.min(...els.map((e) => e.y));
    const maxX = Math.max(...els.map((e) => e.x + e.w)), maxY = Math.max(...els.map((e) => e.y + e.h));
    const pad = 80;
    const scale = clamp(Math.min((size.w - pad) / (maxX - minX), (size.h - pad) / (maxY - minY)), 0.2, 2);
    setCam({ scale, x: size.w / 2 - ((minX + maxX) / 2) * scale, y: size.h / 2 - ((minY + maxY) / 2) * scale });
  };

  // ---- export + imperative commands (driven from menus / pagebar / command palette) ----
  const download = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };
  // Export file base name from the diagram name (falls back to "libredraw"), so exports
  // are told apart instead of every file being libredraw.*.
  const fileBase = () => ((docName || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60)) || "libredraw";
  const exportPng = () => {
    const stage = stageRef.current; if (!stage) return;
    const prev = { ...cam };
    // export at 1:1 world scale so nothing is clipped by the current viewport zoom
    download(stage.toDataURL({ pixelRatio: 2 }), `${fileBase()}.png`);
    void prev;
  };
  const downloadBlob = (data: string, type: string, filename: string) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    download(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const exportJson = () => downloadBlob(JSON.stringify(els, null, 2), "application/json", `${fileBase()}.json`);
  const exportSvg = () => downloadBlob(elsToSvg(els), "image/svg+xml", `${fileBase()}.svg`);
  const exportMermaid = () => downloadBlob(elsToMermaid(els), "text/plain", `${fileBase()}.mmd`);
  // PDF: render the same compact SVG we export as vector into the page, so the file is a
  // few KB (was megabytes: it embedded a 2x raster of the whole stage) and stays sharp at
  // any zoom. svg2pdf measures text via the live DOM, so the SVG is mounted off-screen
  // while rendering. jsPDF is the sanctioned fallback (no Jac/browser PDF primitive).
  const exportPdf = async () => {
    const svgEl = new DOMParser().parseFromString(elsToSvg(els), "image/svg+xml").documentElement as unknown as SVGSVGElement;
    const w = parseFloat(svgEl.getAttribute("width") || "800") || 800;
    const h = parseFloat(svgEl.getAttribute("height") || "600") || 600;
    const holder = document.createElement("div");
    holder.style.cssText = "position:fixed;left:-99999px;top:0;opacity:0;pointer-events:none";
    holder.appendChild(svgEl);
    document.body.appendChild(holder);
    try {
      const pdf = new jsPDF({ orientation: w >= h ? "landscape" : "portrait", unit: "pt", format: [w, h] });
      await (pdf as unknown as { svg: (el: Element, o: object) => Promise<jsPDF> }).svg(svgEl, { x: 0, y: 0, width: w, height: h });
      pdf.save(`${fileBase()}.pdf`);
    } finally {
      holder.remove();
    }
  };

  // Image tool: opening it pops the file picker; the chosen image is placed at the viewport center.
  useEffect(() => {
    if (activeTool === "image") fileRef.current?.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool]);
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) { onToolConsumed(); return; }
    const rd = new FileReader();
    rd.onload = () => {
      const src = rd.result as string;
      const im = new window.Image();
      im.onload = () => {
        const maxDim = 320;
        const k = Math.min(1, maxDim / Math.max(im.width, im.height));
        const w = im.width * k, h = im.height * k;
        const cx = (size.w / 2 - cam.x) / cam.scale - w / 2;
        const cy = (size.h / 2 - cam.y) / cam.scale - h / 2;
        const el: El = { id: uid(), kind: "image", x: cx, y: cy, w, h, stroke: "transparent", fill: "transparent", strokeWidth: 0, src };
        commit((prev) => [...prev, el]);
        setSelId(el.id);
        onToolConsumed();
      };
      im.src = src;
    };
    rd.readAsDataURL(f);
  };

  const cmdN = commandReq?.n ?? 0;
  useEffect(() => {
    if (cmdN <= 0) return;
    // read-only roles (viewer/commenter) can't run mutating commands
    if (!editable && ["undo", "redo", "delete", "deleteId", "duplicate", "nudge", "clear", "group", "ungroup", "renameGroup"].includes(commandReq?.name || "")) return;
    switch (commandReq?.name) {
      case "undo": undo(); break;
      case "redo": redo(); break;
      case "delete": removeSelected(); break;
      case "selectAll": setSelIds(els.map((x) => x.id)); break;
      case "deleteId": { const id = commandReq?.arg; if (id) { removeEl(id); setSelIds((s) => s.filter((x) => x !== id)); } break; }
      case "duplicate": duplicateSelected(); break;
      case "align": { const o = commandReq?.arg; if (typeof o === "string") arrange(o); break; }
      case "toFront": reorderZ("toFront"); break;
      case "toBack": reorderZ("toBack"); break;
      case "forward": reorderZ("forward"); break;
      case "backward": reorderZ("backward"); break;
      case "toggleLock": { const id = commandReq?.arg; toggleLock(typeof id === "string" ? id : undefined); break; }
      case "toggleHide": { const id = commandReq?.arg; toggleHide(typeof id === "string" ? id : undefined); break; }
      case "group": groupSelected(); break;
      case "ungroup": ungroupSelected(); break;
      case "selectGroup": { const gid = commandReq?.arg; if (gid) selectGroup(gid); break; }
      case "selectIds": { const ids = commandReq?.arg; if (Array.isArray(ids) && ids.length) setSelIds(els.filter((e) => ids.includes(e.id)).map((e) => e.id)); break; }
      case "renameGroup": { const a = commandReq?.arg; if (a?.gid) renameGroup(a.gid, a.name || "Group"); break; }
      case "deleteGroup": { const gid = commandReq?.arg; if (gid) { const set = new Set(els.filter((e) => e.groupId === gid).map((e) => e.id)); commit((prev) => prev.filter((p) => !set.has(p.id))); setSelIds((s) => s.filter((x) => !set.has(x))); } break; }
      case "nudge": { const d = commandReq?.arg; if (selId && d) commit((prev) => prev.map((p) => (p.id === selId ? { ...p, x: p.x + (d.dx || 0), y: p.y + (d.dy || 0) } : p))); break; }
      case "clear": commit(() => []); setSelId(null); break;
      case "zoomIn": zoomAroundCenter(1.2); break;
      case "zoomOut": zoomAroundCenter(1 / 1.2); break;
      case "zoomReset": setCam((c) => ({ ...c, scale: 1 })); break;
      case "zoomFit": fit(); break;
      case "exportPng": exportPng(); break;
      case "exportJson": exportJson(); break;
      case "exportSvg": exportSvg(); break;
      case "exportMermaid": exportMermaid(); break;
      case "exportPdf": exportPdf(); break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmdN]);

  // Place a stencil (a library shape group) with its bounding box centred at (cx, cy) in
  // world coords, so it lands where the user pointed instead of at one fixed spot (#43).
  const placeStencil = useCallback((st: any, cx: number, cy: number) => {
    if (!st?.elements?.length) return;
    // a multi-element library item is inserted as ONE named group, so it stays cohesive
    // (and travels whole to anyone the board is shared with)
    const multi = st.elements.length > 1;
    const grouped = st.group !== false;   // imports (Mermaid/scene) skip the single-group wrap
    const gid = multi && grouped ? "g_" + uid() : undefined;
    const gname = multi && grouped ? (st.label || st.name || "Group") : undefined;
    // centre the item's bounding box under (cx, cy)
    let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
    st.elements.forEach((t: any) => { const dx = t.dx || 0, dy = t.dy || 0; minx = Math.min(minx, dx); miny = Math.min(miny, dy); maxx = Math.max(maxx, dx + (t.w || 0)); maxy = Math.max(maxy, dy + (t.h || 0)); });
    const ox = cx - (minx + maxx) / 2, oy = cy - (miny + maxy) / 2;
    // remap every element's id up front so connectors imported alongside their shapes
    // (a Mermaid/Excalidraw diagram) keep their from/to links after the id refresh.
    const idMap = new Map<string, string>();
    st.elements.forEach((t: any) => { if (t.id != null) idMap.set(String(t.id), uid()); });
    const news: El[] = st.elements.map((t: any) => {
      const nid = (t.id != null && idMap.get(String(t.id))) || uid();
      const base: any = {
        id: nid, kind: t.kind, x: ox + (t.dx || 0), y: oy + (t.dy || 0),
        w: t.w, h: t.h, stroke: t.stroke ?? INK, fill: t.fill ?? FILL,
        strokeWidth: t.strokeWidth ?? 2, text: t.text, src: t.src,
        points: t.points, closed: t.closed, smooth: t.smooth, opacity: t.opacity,
        fontSize: t.fontSize, align: t.align, groupId: gid, groupName: gname,
      };
      if (t.kind === "connector") {
        base.from = t.from != null ? (idMap.get(String(t.from)) || null) : null;
        base.to = t.to != null ? (idMap.get(String(t.to)) || null) : null;
        base.fromPoint = t.fromPoint ? { x: ox + t.fromPoint.x, y: oy + t.fromPoint.y } : undefined;
        base.toPoint = t.toPoint ? { x: ox + t.toPoint.x, y: oy + t.toPoint.y } : undefined;
        base.routing = t.routing || "straight";
        base.endArrow = t.endArrow !== false;
        base.startArrow = t.startArrow === true;
        base.endHead = t.endArrow !== false ? "arrow" : "none";
        base.startHead = "none";
        base.label = t.label || "";
      }
      return base;
    });
    commit((prev) => [...prev, ...news]);
    setSelIds(news.map((n) => n.id));   // select the whole inserted group
  }, [commit]);

  // A whole imported diagram (Mermaid / Excalidraw scene) drops at the viewport centre; a
  // single library icon ARMS a click-to-place instead, so successive icons don't stack (#43).
  const insN = insertReq?.n ?? 0;
  // Treat the mount-time insN as already handled. Without this, remounting the canvas on a
  // page switch (the stage is keyed by page id) re-ran this effect with the STALE insN and
  // re-inserted / re-armed the last stencil onto the new page - which hijacked drawing there.
  const seenInsN = useRef(insN);
  useEffect(() => {
    if (insN <= 0 || !editable || insN === seenInsN.current) return;
    seenInsN.current = insN;
    const st = insertReq?.stencil;
    if (!st?.elements?.length) return;
    if (st.group === false) placeStencil(st, (size.w / 2 - cam.x) / cam.scale, (size.h / 2 - cam.y) / cam.scale);
    else setPendingStencil(st);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insN]);

  const selectable = activeTool === "select";

  const renderShape = (el: El) => {
    const common = { perfectDrawEnabled: false } as const;
    if (el.kind === "image") return <URLImage el={el} />;
    if (el.kind === "draw")
      return <Line points={el.points || []} closed={!!el.closed} stroke={el.stroke} strokeWidth={el.strokeWidth}
        fill={el.closed && el.fill && el.fill !== "transparent" ? el.fill : undefined}
        tension={el.smooth ? 0.4 : 0} lineCap="round" lineJoin="round" {...common} />;
    // text has no visible shape, but needs a hittable box so it can be selected / dbl-clicked to edit
    if (el.kind === "text") return <Rect x={0} y={0} width={el.w} height={el.h} fill="rgba(0,0,0,0)" {...common} />;
    if (el.kind === "rectangle" || el.kind === "sticky")
      return <Rect x={0} y={0} width={el.w} height={el.h} fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} cornerRadius={el.kind === "sticky" ? 3 : 6} {...common} />;
    if (el.kind === "ellipse")
      return <Ellipse x={el.w / 2} y={el.h / 2} radiusX={el.w / 2} radiusY={el.h / 2} fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} {...common} />;
    if (el.kind === "diamond" || el.kind === "triangle" || el.kind === "hexagon" || el.kind === "parallelogram") {
      const w = el.w, h = el.h;
      const pts = el.kind === "triangle" ? [w / 2, 0, w, h, 0, h]
        : el.kind === "hexagon" ? [w * 0.25, 0, w * 0.75, 0, w, h / 2, w * 0.75, h, w * 0.25, h, 0, h / 2]
        : el.kind === "parallelogram" ? [w * 0.25, 0, w, 0, w * 0.75, h, 0, h]
        : [w / 2, 0, w, h / 2, w / 2, h, 0, h / 2];
      return <Line points={pts} closed fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} lineJoin="round" {...common} />;
    }
    if (el.kind === "cylinder") {
      const ry = Math.min(el.h * 0.16, 16);
      return (<>
        <Rect x={0} y={ry} width={el.w} height={el.h - ry * 2} fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} {...common} />
        <Ellipse x={el.w / 2} y={el.h - ry} radiusX={el.w / 2} radiusY={ry} fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} {...common} />
        <Ellipse x={el.w / 2} y={ry} radiusX={el.w / 2} radiusY={ry} fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} {...common} />
      </>);
    }
    // document / class / package / cube / cloud - path-based shapes (see geometry.shapePath),
    // so a palette shape inserts as its real shape instead of a plain rectangle.
    if (PATH_KINDS.has(el.kind)) {
      const d = shapePath(el.kind, el.w, el.h) || "";
      return <Path data={d} fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth} lineJoin="round" {...common} />;
    }
    if (el.kind === "frame") {
      // Border only (no fill) so the interior stays click-through for drawing/marquee inside;
      // the border and the top-left name bar are the grab/select targets, and dragging the
      // frame carries the elements it contains (see onDragStart). Excalidraw-parity container.
      const name = el.text || "Frame";
      const barW = Math.min(el.w, name.length * 7 + 16);
      return (<>
        <Rect x={0} y={0} width={el.w} height={el.h} stroke={el.stroke || "#94a3b8"} strokeWidth={el.strokeWidth || 1.5} cornerRadius={4} dash={[6, 4]} hitStrokeWidth={14} {...common} />
        <Rect x={0} y={-20} width={barW} height={18} fill={el.stroke || "#94a3b8"} cornerRadius={4} {...common} />
        <Text x={6} y={-17} width={barW - 8} text={name} fontSize={11} fontStyle="600" fontFamily="Inter, system-ui, sans-serif" fill="#ffffff" listening={false} {...common} />
      </>);
    }
    if (el.kind === "table") {
      const rows = Math.max(1, el.rows || 3), cols = Math.max(1, el.cols || 3);
      const cw = el.w / cols, ch = el.h / rows;
      const cells = el.cells || [];
      const grid: React.ReactNode[] = [];
      for (let c = 1; c < cols; c++) grid.push(<Line key={"v" + c} points={[c * cw, 0, c * cw, el.h]} stroke={el.stroke || "#94a3b8"} strokeWidth={1} listening={false} perfectDrawEnabled={false} />);
      for (let r = 1; r < rows; r++) grid.push(<Line key={"h" + r} points={[0, r * ch, el.w, r * ch]} stroke={el.stroke || "#94a3b8"} strokeWidth={1} listening={false} perfectDrawEnabled={false} />);
      for (let i = 0; i < rows * cols; i++) {
        const t = cells[i]; if (!t) continue;
        if (editId === el.id && editCell === i) continue; // covered by the inline cell editor
        const r = Math.floor(i / cols), c = i % cols;
        grid.push(<Text key={"t" + i} x={c * cw + 4} y={r * ch + 4} width={cw - 8} height={ch - 8} text={t} fontSize={fontSizeOf(el, 13)} fontFamily={fontStack(el.fontFamily)} fill="#1f2430" verticalAlign="middle" listening={false} perfectDrawEnabled={false} />);
      }
      return (<>
        <Rect x={0} y={0} width={el.w} height={el.h} fill={el.fill || "#ffffff"} stroke={el.stroke || "#94a3b8"} strokeWidth={el.strokeWidth || 1.5} cornerRadius={3} {...common} />
        {grid}
      </>);
    }
    return null;
  };

  const renderEl = (el: El, isDraft: boolean) => (
    <KonvaGroup
      key={el.id}
      id={el.id}
      x={el.x}
      y={el.y}
      rotation={el.rotation || 0}
      opacity={el.opacity ?? 1}
      draggable={selectable && !isDraft && editable && !el.locked}
      onMouseEnter={(e: any) => {
        if (!isDraft && (selectable || activeTool === "connector")) { clearTimeout(hoverClear.current); setHoverId(el.id); }
        if (activeTool === "connector") { const st = e.target.getStage(); if (st) st.container().style.cursor = "crosshair"; }
      }}
      onMouseLeave={() => { if (!connecting.current) hoverClear.current = setTimeout(() => setHoverId((h) => (h === el.id ? null : h)), 80); }}
      onMouseDown={(e: any) => {
        // connector tool: let the press bubble to the stage handler, which starts the arrow.
        // Select mode: Shift toggles this element in the multi-selection; plain click selects it
        // (unless it's already part of a multi-selection being dragged).
        if (selectable) {
          e.cancelBubble = true;
          const gm = groupMemberIds(el.id);   // whole group when this element is grouped, else [el.id]
          if (e.evt?.shiftKey) setSelIds((s) => (gm.every((id) => s.includes(id)) ? s.filter((x) => !gm.includes(x)) : Array.from(new Set([...s, ...gm]))));
          else if (!selIds.includes(el.id)) setSelIds(gm);
        }
      }}
      onDragStart={() => { const gm = groupMemberIds(el.id); let drag = selIds.includes(el.id) ? selIds.slice() : gm.slice(); if (!selIds.includes(el.id)) setSelIds(gm); if (el.kind === "frame") drag = Array.from(new Set([...drag, ...framedChildren(el)])); dragBase.current = els.filter((x) => drag.includes(x.id) && x.kind !== "connector").map((x) => ({ id: x.id, x: x.x, y: x.y })); }}
      onDragMove={(e: any) => {
        const r = alignSnap(el, e.target.x(), e.target.y()); e.target.position({ x: r.x, y: r.y }); setGuides(r.guides);
        const base = dragBase.current;
        if (base && base.length > 1) { // drag the whole selection together
          const me = base.find((b) => b.id === el.id); if (me) { const ddx = r.x - me.x, ddy = r.y - me.y; const stage = stageRef.current; base.forEach((b) => { if (b.id === el.id) return; const n = stage?.findOne("#" + b.id); if (n) n.position({ x: b.x + ddx, y: b.y + ddy }); }); }
        }
      }}
      onDragEnd={(e: any) => {
        const r = alignSnap(el, e.target.x(), e.target.y()); e.target.position({ x: r.x, y: r.y }); setGuides([]);
        const base = dragBase.current || [{ id: el.id, x: el.x, y: el.y }];
        const me = base.find((b) => b.id === el.id); const ddx = me ? r.x - me.x : 0, ddy = me ? r.y - me.y : 0;
        const map = new Map(base.map((b) => [b.id, b]));
        // free arrows selected alongside the dragged shape(s) move by the same delta
        const selSet = new Set(selIds.includes(el.id) ? selIds : []);
        commit((prev) => prev.map((p) => {
          const b = map.get(p.id);
          if (b) return { ...p, x: b.x + ddx, y: b.y + ddy };
          if (p.kind === "connector" && selSet.has(p.id)) return translateEl(p, ddx, ddy);
          return p;
        }));
        dragBase.current = null;
      }}
      onDblClick={(e: any) => {
        if (!editable) return;
        if (el.kind === "text" || el.kind === "sticky") { setSelId(el.id); setEditId(el.id); setEditCell(-1); }
        else if (el.kind === "table") {
          const p = e.target.getStage()?.getRelativePointerPosition();
          const rows = Math.max(1, el.rows || 3), cols = Math.max(1, el.cols || 3);
          let idx = 0;
          if (p) {
            const c = Math.min(cols - 1, Math.max(0, Math.floor((p.x - el.x) / (el.w / cols))));
            const r = Math.min(rows - 1, Math.max(0, Math.floor((p.y - el.y) / (el.h / rows))));
            idx = r * cols + c;
          }
          setSelId(el.id); setEditId(el.id); setEditCell(idx);
        }
        // any other box shape (rectangle, ellipse, diamond, ...): double-click drops a
        // centered text label straight into edit - like Excalidraw. Excludes image/draw/frame.
        else if (canLabel(el)) { setSelId(el.id); setEditId(el.id); setEditCell(-1); }
      }}
      onDblTap={(e: any) => {
        if (!editable) return;
        if (el.kind === "text" || el.kind === "sticky") { setSelId(el.id); setEditId(el.id); setEditCell(-1); }
        else if (el.kind === "table") {
          const p = e.target.getStage()?.getRelativePointerPosition();
          const rows = Math.max(1, el.rows || 3), cols = Math.max(1, el.cols || 3);
          let idx = 0;
          if (p) {
            const c = Math.min(cols - 1, Math.max(0, Math.floor((p.x - el.x) / (el.w / cols))));
            const r = Math.min(rows - 1, Math.max(0, Math.floor((p.y - el.y) / (el.h / rows))));
            idx = r * cols + c;
          }
          setSelId(el.id); setEditId(el.id); setEditCell(idx);
        }
        // any other box shape (rectangle, ellipse, diamond, ...): double-click drops a
        // centered text label straight into edit - like Excalidraw. Excludes image/draw/frame.
        else if (canLabel(el)) { setSelId(el.id); setEditId(el.id); setEditCell(-1); }
      }}
      // NOTE: resize is committed ONCE for the whole selection on the <Transformer> below,
      // not per node - a multi-object resize must be a single undoable action (#40).
    >
      {renderShape(el)}
      {/* connector affordance: dashed ring on the hovered/target shape and the chosen source
          (arrow tool, or while dragging an arrow out of a hover port in select mode) */}
      {(activeTool === "connector" || connDraw.current) && el.kind !== "connector" && (hoverId === el.id || connectFrom === el.id) && (
        <Rect x={-3} y={-3} width={el.w + 6} height={el.h + 6} stroke={INK} strokeWidth={1.5} dash={[4, 3]} cornerRadius={8} listening={false} perfectDrawEnabled={false} />
      )}
      {(el.kind === "text" || el.kind === "sticky") && el.id !== editId && (
        el.fontSize
          // imported text (library icon captions, scene/Mermaid labels): honour its own size,
          // colour and alignment, but DON'T impose the authored box as a fixed Konva width. The
          // box was measured against Excalidraw's font metrics; Konva's Inter metrics run a hair
          // wider, and a fixed-width Konva Text truncates (and drops) any overflow - even with
          // wrap="none" (Konva treats no-wrap + fixed width as "ellipsize this line") - so the
          // last character vanished on the canvas while every export kept it. Rendering at natural
          // width (like the SVG/PNG export, which sets no width) keeps the whole caption on one
          // line; the element's own x/y already position it, and captions are authored to fit.
          ? <Text x={0} y={el.h ? el.h / 2 : 0} offsetY={el.fontSize ? el.fontSize * 0.5 : 0} text={el.text || ""} fontSize={el.fontSize} fontStyle="600" fontFamily={fontStack(el.fontFamily)} fill={el.stroke || "#1f2430"} align={el.align || "left"} wrap="none" listening={false} />
          : <Text x={el.kind === "sticky" ? 10 : 2} y={el.kind === "sticky" ? 10 : 4} width={el.w - (el.kind === "sticky" ? 20 : 4)} text={el.text || ""} fontSize={fontSizeOf(el)} fontFamily={fontStack(el.fontFamily)} fill="#1f2430" listening={false} />
      )}
      {/* centered in-shape label for box shapes (rectangle/ellipse/diamond/...) */}
      {canLabel(el) && el.text && el.id !== editId && (
        <Text x={4} y={0} width={el.w - 8} height={el.h} text={el.text} fontSize={el.fontSize || 14} fontFamily={fontStack(el.fontFamily)} fill="#1f2430" align="center" verticalAlign="middle" listening={false} perfectDrawEnabled={false} />
      )}
    </KonvaGroup>
  );

  const renderConnector = (c: El, connPolys: { id: string; pts: Pt[]; bezier: boolean }[] = []) => {
    const path = connectorPath(c, els);
    if (!path) return null;
    const { pts, bezier, tension, anchors } = path;
    // line jumps: hop this connector over the OTHER (non-curved) connectors it crosses
    const drawPts = bezier ? pts : withJumps(pts, connPolys.filter((p) => p.id !== c.id && !p.bezier).map((p) => p.pts));
    const flat = drawPts.flatMap((p) => [p.x, p.y]);
    const sel = selId === c.id;
    const stroke = sel ? INK : (c.stroke || "#64748b");
    const mid = polyMid(pts);
    const label = c.label || "";
    const lw = label ? label.length * 6.6 + 12 : 0;
    const dash = c.dotted ? [2, 6] : c.dashed ? [9, 7] : undefined;
    // arrowhead type per end (backward-compatible with the old start/end booleans)
    const endHead = c.endHead || (c.endArrow !== false ? "arrow" : "none");
    const startHead = c.startHead || (c.startArrow ? "arrow" : "none");
    const ep = pts[pts.length - 1], ep2 = pts[pts.length - 2] || pts[0];
    const sp = pts[0], sp2 = pts[1] || pts[pts.length - 1];
    const angEnd = Math.atan2(ep.y - ep2.y, ep.x - ep2.x) * 180 / Math.PI;
    const angStart = Math.atan2(sp.y - sp2.y, sp.x - sp2.x) * 180 / Math.PI;
    const head = (type: string, pos: Pt, angDeg: number, key: string) => {
      if (type === "circle") return <Circle key={key} x={pos.x} y={pos.y} radius={5} fill="#ffffff" stroke={stroke} strokeWidth={2} listening={false} perfectDrawEnabled={false} />;
      if (type === "diamond") return <Line key={key} x={pos.x} y={pos.y} rotation={angDeg} points={[7, 0, 0, -5, -7, 0, 0, 5]} closed fill={stroke} listening={false} perfectDrawEnabled={false} />;
      // ER crow's-foot family. Local +x points toward the shape; parts sit back along the line.
      // recipe = list of [kind, x-offset]: "bar" = cardinality bar |, "ring" = optional ○, "foot" = many ≺
      const ER: Record<string, [string, number][]> = {
        one: [["bar", -9]],
        onlyOne: [["bar", -8], ["bar", -13]],
        many: [["foot", 0]],
        zeroOrOne: [["ring", -16], ["bar", -9]],
        oneOrMany: [["bar", -16], ["foot", 0]],
        zeroOrMany: [["ring", -18], ["foot", 0]],
      };
      const recipe = ER[type];
      if (recipe) {
        const parts: any[] = [];
        recipe.forEach(([kind, x], i) => {
          if (kind === "bar") parts.push(<Line key={i} points={[x, -6, x, 6]} stroke={stroke} strokeWidth={2} listening={false} perfectDrawEnabled={false} />);
          else if (kind === "ring") parts.push(<Circle key={i} x={x} y={0} radius={4} fill="#ffffff" stroke={stroke} strokeWidth={2} listening={false} perfectDrawEnabled={false} />);
          else { // crow's foot: three prongs fanning from an apex back along the line to the tip
            parts.push(<Line key={`${i}a`} points={[-13, 0, 0, -7]} stroke={stroke} strokeWidth={2} listening={false} perfectDrawEnabled={false} />);
            parts.push(<Line key={`${i}b`} points={[-13, 0, 0, 0]} stroke={stroke} strokeWidth={2} listening={false} perfectDrawEnabled={false} />);
            parts.push(<Line key={`${i}c`} points={[-13, 0, 0, 7]} stroke={stroke} strokeWidth={2} listening={false} perfectDrawEnabled={false} />);
          }
        });
        return <KonvaGroup key={key} x={pos.x} y={pos.y} rotation={angDeg} listening={false}>{parts}</KonvaGroup>;
      }
      return null;
    };
    return (
      <React.Fragment key={c.id}>
        {sel && (
          <Line points={flat} bezier={bezier} tension={tension} stroke="rgba(79,70,229,0.16)" strokeWidth={(c.strokeWidth || 2) + 6} lineCap="round" lineJoin="round" listening={false} perfectDrawEnabled={false} />
        )}
        <Arrow
          points={flat}
          bezier={bezier}
          tension={tension}
          stroke={stroke}
          strokeWidth={c.strokeWidth || 2}
          dash={dash}
          pointerAtBeginning={startHead === "arrow"}
          pointerAtEnding={endHead === "arrow"}
          pointerLength={9}
          pointerWidth={8}
          lineCap="round"
          lineJoin="round"
          hitStrokeWidth={22}
          onMouseDown={(e: any) => {
            if (!selectable) return;
            e.cancelBubble = true;
            // Shift toggles the arrow in/out of the multi-selection; plain click selects it
            // (kept if already part of a selection, so a group drag isn't broken) - same as shapes.
            if (e.evt?.shiftKey) setSelIds((s) => (s.includes(c.id) ? s.filter((x) => x !== c.id) : Array.from(new Set([...s, c.id]))));
            else if (!selIds.includes(c.id)) setSelId(c.id);
          }}
          onDblClick={(e: any) => { e.cancelBubble = true; setSelId(c.id); if (editable) setEditId(c.id); }}
          onDblTap={(e: any) => { e.cancelBubble = true; setSelId(c.id); if (editable) setEditId(c.id); }}
        />
        {head(endHead, ep, angEnd, "eh")}
        {head(startHead, sp, angStart, "sh")}
        {/* when selected: an invisible thick overlay on the body - grab anywhere and drag to pull out
            a bend. Rendered below the label (so the label stays grabbable) but above the visible line. */}
        {sel && selectable && editable && c.id !== editId && (
          <Line points={flat} bezier={bezier} tension={tension} stroke="transparent" strokeWidth={1} hitStrokeWidth={16} draggable
            onMouseEnter={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = "grab"; }}
            onMouseLeave={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = cursorRef.current; }}
            onMouseDown={(e: any) => { e.cancelBubble = true; }}
            onDragStart={(e: any) => {
              const st = e.target.getStage(); if (!st) return;
              const pt = st.getRelativePointerPosition();
              let bi = 0, bd = Infinity;
              for (let i = 0; i < anchors.length - 1; i++) { const d = segDist(pt, anchors[i], anchors[i + 1]); if (d < bd) { bd = d; bi = i; } }
              bendRef.current = bi;
              setEls((prev) => prev.map((el) => { if (el.id !== c.id) return el; const wp = [...(el.waypoints || [])]; wp.splice(bi, 0, pt); return { ...el, waypoints: wp }; }));
            }}
            onDragMove={(e: any) => {
              e.target.position({ x: 0, y: 0 });
              const st = e.target.getStage(); const i = bendRef.current; if (!st || i == null) return;
              const pt = st.getRelativePointerPosition();
              setEls((prev) => prev.map((el) => (el.id === c.id ? { ...el, waypoints: (el.waypoints || []).map((w, k) => (k === i ? pt : w)) } : el)));
            }}
            onDragEnd={(e: any) => {
              e.target.position({ x: 0, y: 0 });
              const st = e.target.getStage(); const i = bendRef.current;
              if (st && i != null) { const pt = st.getRelativePointerPosition(); commit((prev) => prev.map((el) => (el.id === c.id ? { ...el, waypoints: (el.waypoints || []).map((w, k) => (k === i ? pt : w)) } : el))); }
              bendRef.current = null;
            }}
          />
        )}
        {label && c.id !== editId && (() => {
          const lp = c.labelPos || mid;
          return (
            <KonvaGroup x={lp.x} y={lp.y} listening={sel} draggable={sel && editable}
              onMouseDown={(e: any) => { if (selectable) { e.cancelBubble = true; setSelId(c.id); } }}
              onDblClick={(e: any) => { e.cancelBubble = true; setSelId(c.id); if (editable) setEditId(c.id); }}
              onDblTap={(e: any) => { e.cancelBubble = true; setSelId(c.id); if (editable) setEditId(c.id); }}
              onDragEnd={(e: any) => { const np = { x: e.target.x(), y: e.target.y() }; commit((prev) => prev.map((el) => (el.id === c.id ? { ...el, labelPos: np } : el))); }}
            >
              <Rect x={-lw / 2} y={-9} width={lw} height={18} fill="#ffffff" stroke={stroke} strokeWidth={sel ? 1 : 0} cornerRadius={4} perfectDrawEnabled={false} />
              <Text x={-lw / 2} y={-5} width={lw} align="center" text={label} fontSize={12} fontFamily={fontStack(c.fontFamily)} fill="#334155" listening={false} />
            </KonvaGroup>
          );
        })()}
      </React.Fragment>
    );
  };

  // inline text editor overlay (screen coords = world * scale + camera)
  const editing = editId ? els.find((e) => e.id === editId) : null;
  const isConnEdit = editing?.kind === "connector";
  let connMid: Pt | null = null;
  if (isConnEdit && editing) {
    const path = connectorPath(editing, els);
    if (path) connMid = editing.labelPos || polyMid(path.pts);
  }
  const isTableEdit = editing?.kind === "table" && editCell >= 0;
  const isShapeLabelEdit = !!editing && canLabel(editing);
  let tableCellStyle: React.CSSProperties | null = null;
  if (editing && isTableEdit) {
    const cols = Math.max(1, editing.cols || 3), rows = Math.max(1, editing.rows || 3);
    const cw = editing.w / cols, chh = editing.h / rows;
    const col = editCell % cols, row = Math.floor(editCell / cols);
    tableCellStyle = {
      position: "absolute",
      left: (editing.x + col * cw) * cam.scale + cam.x + 3,
      top: (editing.y + row * chh) * cam.scale + cam.y + 3,
      width: (cw - 6) * cam.scale, height: (chh - 6) * cam.scale,
      fontSize: 13 * cam.scale, lineHeight: 1.3,
      fontFamily: fontStack(editing.fontFamily), color: "#1f2430",
      background: "#ffffff", border: "1px solid #4f46e5", borderRadius: 2,
      outline: "none", resize: "none", padding: "1px 3px", margin: 0, overflow: "hidden",
    };
  }
  const editStyle: React.CSSProperties | null = !editing ? null : isTableEdit ? tableCellStyle : isConnEdit ? (connMid && {
    position: "absolute",
    left: connMid.x * cam.scale + cam.x - 60,
    top: connMid.y * cam.scale + cam.y - 11,
    width: 120, height: 22, textAlign: "center",
    fontSize: 12, lineHeight: "20px", fontFamily: fontStack(editing.fontFamily), color: "#334155",
    background: "#ffffff", border: "1px solid #4f46e5", borderRadius: 4,
    outline: "none", resize: "none", padding: "0 4px", margin: 0, overflow: "hidden",
  }) : isShapeLabelEdit ? {
    // centered label overlay sitting over the shape's box
    position: "absolute",
    left: editing.x * cam.scale + cam.x + 4 * cam.scale,
    top: editing.y * cam.scale + cam.y,
    width: (editing.w - 8) * cam.scale, height: editing.h * cam.scale,
    fontSize: (editing.fontSize || 14) * cam.scale, lineHeight: 1.3,
    fontFamily: fontStack(editing.fontFamily), color: "#1f2430", textAlign: "center",
    paddingTop: Math.max(0, (editing.h * cam.scale - (editing.fontSize || 14) * cam.scale * 1.3) / 2),
    background: "transparent", border: "none", outline: "none", resize: "none", paddingLeft: 0, paddingRight: 0, margin: 0, overflow: "hidden",
  } : {
    position: "absolute",
    left: editing.x * cam.scale + cam.x + (editing.kind === "sticky" ? 10 : 2) * cam.scale,
    top: editing.y * cam.scale + cam.y + (editing.kind === "sticky" ? 8 : 3) * cam.scale,
    width: (editing.w - (editing.kind === "sticky" ? 20 : 4)) * cam.scale,
    height: (editing.h - (editing.kind === "sticky" ? 16 : 6)) * cam.scale,
    fontSize: fontSizeOf(editing) * cam.scale,
    lineHeight: 1.3,
    fontFamily: fontStack(editing.fontFamily),
    color: "#1f2430",
    background: editing.kind === "sticky" ? NOTE : "transparent",
    border: "none", outline: "none", resize: "none", padding: 0, margin: 0, overflow: "hidden",
  };

  const zoomPct = Math.round(cam.scale * 100);
  // Only show the starter card when idle (select tool) - otherwise it intercepts
  // clicks meant for the canvas (dropping a comment, drawing a shape, etc.).
  const showStarter = els.length === 0 && activeTool === "select" && !draft;

  // a floating quick-action bar above the current shape selection (fill swatches +
  // duplicate + delete). Positioned from the selection's bounding box in screen space.
  const selShapes = els.filter((e) => selIds.includes(e.id) && e.kind !== "connector");
  const selBar = (editable && activeTool === "select" && !editId && selShapes.length > 0)
    ? (() => {
        const minX = Math.min(...selShapes.map((e) => e.x));
        const minY = Math.min(...selShapes.map((e) => e.y));
        const maxX = Math.max(...selShapes.map((e) => e.x + e.w));
        return { x: ((minX + maxX) / 2) * cam.scale + cam.x, y: minY * cam.scale + cam.y };
      })()
    : null;
  const applyFill = (c: string) => { const s = new Set(selIds); commit((prev) => prev.map((p) => (s.has(p.id) ? { ...p, fill: c } : p))); };

  // A single selected table gets +/- controls at its right edge (columns) and bottom edge (rows).
  const selTable = (editable && activeTool === "select" && !editId && selIds.length === 1)
    ? els.find((e) => e.id === selIds[0] && e.kind === "table") : null;

  return (
    <div ref={boxRef} tabIndex={-1} onContextMenu={openContextMenu} className="absolute inset-0 outline-none">
      {size.w > 0 && (
        <Stage
          ref={stageRef}
          width={size.w} height={size.h}
          scaleX={cam.scale} scaleY={cam.scale} x={cam.x} y={cam.y}
          draggable={activeTool === "pan"}
          onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          onDragEnd={(e: any) => { if (e.target === e.target.getStage()) setCam((c) => ({ ...c, x: e.target.x(), y: e.target.y() })); }}
          style={{ touchAction: "none", cursor: toolCursor }}
        >
          <Layer>
            {(() => {
              const conns = els.filter((e) => e.kind === "connector" && !e.hidden);
              const connPolys = conns.map((c) => { const p = connectorPath(c, els); return p ? { id: c.id, pts: p.pts, bezier: p.bezier } : null; }).filter(Boolean) as { id: string; pts: Pt[]; bezier: boolean }[];
              return conns.map((c) => renderConnector(c, connPolys));
            })()}
            {connDraw.current && preview && (() => {
              // live preview while drawing an arrow: from the bound source anchor (or the free
              // start point) to the cursor
              const start = connDraw.current;
              const src = start.from ? els.find((e) => e.id === start.from) : null;
              const a = src ? anchor(src, preview) : start.pt;
              return <Arrow points={[a.x, a.y, preview.x, preview.y]} stroke="#94a3b8" strokeWidth={2} dash={[6, 4]} pointerLength={8} pointerWidth={8} listening={false} />;
            })()}
            {els.filter((e) => e.kind === "frame" && !e.hidden).map((el) => renderEl(el, false))}
            {els.filter((e) => e.kind !== "connector" && e.kind !== "frame" && !e.hidden).map((el) => renderEl(el, false))}
            {draft && renderEl(draft, true)}
            {inkPts && inkPts.length >= 4 && (
              <Line points={inkPts} stroke={INK} strokeWidth={2} tension={0.4}
                lineCap="round" lineJoin="round" listening={false} perfectDrawEnabled={false} />
            )}
            {/* Hover connection ports (Excalidraw/draw.io feel): in select mode, hovering a shape
                shows small dots at its edge midpoints - press one and drag to draw an arrow without
                switching to the arrow tool. Hidden once a drag starts (the live preview takes over). */}
            {selectable && editable && !editId && !connDraw.current && !draft && (() => {
              const el = els.find((e) => e.id === hoverId && e.kind !== "connector" && e.kind !== "draw" && e.kind !== "frame" && e.kind !== "image" && e.kind !== "text");
              // skip the actively-selected shape: its Transformer already owns the edge midpoints
              // (resize anchors), so ports there would collide. Ports are a hover-on-unselected cue.
              if (!el || selIds.includes(el.id)) return null;
              // sit each port just outside the corresponding edge midpoint (draw.io feel), clear of
              // the shape border. The arrow still binds to the shape (from=el.id) on release.
              const g = 8;
              const ports: [number, number][] = [[el.x + el.w / 2, el.y - g], [el.x + el.w + g, el.y + el.h / 2], [el.x + el.w / 2, el.y + el.h + g], [el.x - g, el.y + el.h / 2]];
              return ports.map(([px, py], i) => (
                <Circle key={"port" + i} x={px} y={py} radius={4.5} fill="#ffffff" stroke={INK} strokeWidth={1.5} hitStrokeWidth={12} perfectDrawEnabled={false}
                  onMouseEnter={(e: any) => { clearTimeout(hoverClear.current); const st = e.target.getStage(); if (st) st.container().style.cursor = "crosshair"; }}
                  onMouseLeave={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = cursorRef.current; hoverClear.current = setTimeout(() => setHoverId((h) => (h === el.id ? null : h)), 80); }}
                  onMouseDown={(e: any) => { e.cancelBubble = true; connDraw.current = { from: el.id, pt: { x: px, y: py } }; setConnectFrom(el.id); setPreview({ x: px, y: py }); }}
                />
              ));
            })()}
            {/* live alignment guides while a shape is being dragged */}
            {guides.map((g, i) => (
              <Line key={"guide" + i} points={g.pts} stroke="#4f46e5" strokeWidth={1} dash={[4, 4]} listening={false} perfectDrawEnabled={false} />
            ))}
            {/* rubber-band marquee */}
            {marqueeRect && (
              <Rect x={marqueeRect.x} y={marqueeRect.y} width={marqueeRect.w} height={marqueeRect.h} fill="rgba(79,70,229,0.08)" stroke="#4f46e5" strokeWidth={1} dash={[4, 3]} listening={false} perfectDrawEnabled={false} />
            )}
            {/* live collaborator cursors (world coords; counter-scaled so they stay a constant size at any zoom) */}
            {(remoteCursors || []).map((c) => (
              <KonvaGroup key={"rc-" + c.id} x={c.x} y={c.y} scaleX={1 / cam.scale} scaleY={1 / cam.scale} listening={false}>
                <Line points={[0, 0, 0, 17, 5, 12.5, 8.5, 20, 11, 19, 7.5, 11.5, 13, 11]} closed fill={c.color} stroke="#ffffff" strokeWidth={1.2} perfectDrawEnabled={false} />
                <Rect x={13} y={11} width={c.name.length * 6.4 + 12} height={17} fill={c.color} cornerRadius={4} perfectDrawEnabled={false} />
                <Text x={19} y={14} text={c.name} fontSize={11} fontStyle="600" fill="#ffffff" />
              </KonvaGroup>
            ))}
            {/* selected connector - minimal handles (Excalidraw feel):
                • 2 endpoint dots (drag to reconnect / detach)
                • grab the line body anywhere to pull out a bend
                • a single midpoint hint when the line is still straight
                • solid dots for bends you've already added (drag to move / dbl-click to remove) */}
            {selectable && !editId && editable && (() => {
              const c = els.find((e) => e.id === selId && e.kind === "connector");
              if (!c) return null;
              const path = connectorPath(c, els);
              if (!path) return null;
              const { anchors, pts } = path;
              const hasWp = !!(c.waypoints && c.waypoints.length);
              // padded so reconnecting an endpoint onto a shape's outline/edge binds (#61)
              const shapeUnder = (pt: Pt) => bindShapeAt(pt);
              const setEnd = (which: "from" | "to", pt: Pt, bind: boolean, use: (u: (prev: El[]) => El[]) => void) =>
                use((prev) => prev.map((el) => {
                  if (el.id !== c.id) return el;
                  const target = bind ? shapeUnder(pt) : null;
                  // dropping on a shape pins the endpoint to that shape's nearest side (fixed port)
                  return { ...el, [which]: target ? target.id : null, [which + "Point"]: target ? undefined : pt, [which + "Side"]: target ? nearestSide(target, pt) : undefined };
                }));
              const setWp = (j: number, pt: Pt, use: (u: (prev: El[]) => El[]) => void) =>
                use((prev) => prev.map((el) => (el.id === c.id ? { ...el, waypoints: (el.waypoints || []).map((w, k) => (k === j ? pt : w)) } : el)));
              const nodes: any[] = [];
              // endpoint handles
              const endHandle = (which: "from" | "to", pt: Pt) => (
                <Circle key={"end-" + which} x={pt.x} y={pt.y} radius={6} fill="#ffffff" stroke={INK} strokeWidth={2} draggable
                  onMouseEnter={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = "grab"; }}
                  onMouseLeave={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = cursorRef.current; }}
                  onMouseDown={(e: any) => { e.cancelBubble = true; }}
                  onDragMove={(e: any) => setEnd(which, { x: e.target.x(), y: e.target.y() }, false, setEls)}
                  onDragEnd={(e: any) => setEnd(which, { x: e.target.x(), y: e.target.y() }, true, commit)}
                />
              );
              nodes.push(endHandle("from", anchors[0]));
              nodes.push(endHandle("to", anchors[anchors.length - 1]));
              // existing bends: solid dots (drag to move, dbl-click to remove)
              (c.waypoints || []).forEach((w, j) => nodes.push(
                <Circle key={"wp-" + j} x={w.x} y={w.y} radius={5} fill={INK} stroke="#ffffff" strokeWidth={1.5} draggable
                  onMouseEnter={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = "grab"; }}
                  onMouseLeave={(e: any) => { const st = e.target.getStage(); if (st) st.container().style.cursor = cursorRef.current; }}
                  onMouseDown={(e: any) => { e.cancelBubble = true; }}
                  onDragMove={(e: any) => setWp(j, { x: e.target.x(), y: e.target.y() }, setEls)}
                  onDragEnd={(e: any) => setWp(j, { x: e.target.x(), y: e.target.y() }, commit)}
                  onDblClick={(e: any) => { e.cancelBubble = true; commit((prev) => prev.map((el) => (el.id === c.id ? { ...el, waypoints: (el.waypoints || []).filter((_, k) => k !== j) } : el))); }}
                  onDblTap={(e: any) => { e.cancelBubble = true; commit((prev) => prev.map((el) => (el.id === c.id ? { ...el, waypoints: (el.waypoints || []).filter((_, k) => k !== j) } : el))); }}
                />
              ));
              // single "drag to bend" hint while the line is still straight (visual only; the grab line handles it)
              if (!hasWp) { const m = polyMid(pts); nodes.push(<Circle key="bendhint" x={m.x} y={m.y} radius={4.5} fill="#ffffff" stroke={INK} strokeWidth={1.5} opacity={0.85} listening={false} perfectDrawEnabled={false} />); }
              return nodes;
            })()}
            <Transformer ref={trRef} rotateEnabled={true} rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]} rotationSnapTolerance={4} keepRatio={false} anchorSize={8} anchorStroke={INK} anchorFill="#ffffff" anchorCornerRadius={2} borderStroke={INK} borderStrokeWidth={1.5}
              boundBoxFunc={(oldB: any, newB: any) => (newB.width < 20 || newB.height < 20 ? oldB : newB)}
              onTransformEnd={() => {
                // One transform of a selection = ONE history entry covering EVERY node, so a single
                // undo restores the whole selection and never leaves a half-scaled composite (#40).
                // Each node's live scale is baked into w/h and reset to 1; a text element scales its
                // fontSize by the geometric mean so an imported icon's caption grows with its
                // artwork instead of staying tiny (#41). Rotation (#48) is stored as a degrees field
                // and kept on the node (the render re-applies it), and Konva's rotate handle already
                // updated x/y to pivot about the centre, so we persist those too.
                const tr = trRef.current; if (!tr) return;
                const ns = (tr.nodes() as any[]) || [];
                const upd = new Map<string, { x: number; y: number; sx: number; sy: number; rot: number }>();
                ns.forEach((n) => { const sx = n.scaleX(), sy = n.scaleY(); n.scaleX(1); n.scaleY(1); upd.set(n.id(), { x: n.x(), y: n.y(), sx, sy, rot: n.rotation() }); });
                if (upd.size === 0) return;
                commit((prev) => prev.map((p) => {
                  const u = upd.get(p.id); if (!u) return p;
                  const next: El = { ...p, x: u.x, y: u.y, w: Math.max(4, p.w * u.sx), h: Math.max(4, p.h * u.sy), rotation: ((u.rot % 360) + 360) % 360 };
                  if (p.fontSize) next.fontSize = Math.max(1, p.fontSize * Math.sqrt(Math.abs(u.sx * u.sy)));
                  // a freehand stroke lives in `points`; scale them with the box or the ink
                  // would keep its old size inside a resized frame.
                  if (p.kind === "draw" && p.points) next.points = p.points.map((v, i) => v * (i % 2 === 0 ? u.sx : u.sy));
                  return next;
                }));
              }}
            />
          </Layer>
        </Stage>
      )}

      {editing && editStyle && (
        <textarea
          autoFocus
          defaultValue={(isTableEdit ? (editing.cells || [])[editCell] : isConnEdit ? editing.label : editing.text) || ""}
          style={editStyle}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={(e) => {
            const v = e.target.value;
            if (isTableEdit) {
              const idx = editCell;
              commit((prev) => prev.map((p) => {
                if (p.id !== editing.id) return p;
                const rows = Math.max(1, p.rows || 3), cols = Math.max(1, p.cols || 3);
                const cells = (p.cells || []).slice();
                while (cells.length < rows * cols) cells.push("");
                cells[idx] = v;
                return { ...p, cells };
              }));
            } else {
              const field = isConnEdit ? "label" : "text";
              commit((prev) => prev.map((p) => (p.id === editing.id ? { ...p, [field]: v } : p)));
            }
            setEditId(null); setEditCell(-1);
          }}
          onKeyDown={(e) => { if (e.key === "Escape") { (e.target as HTMLTextAreaElement).blur(); } if (e.key === "Enter" && (isConnEdit || editing.kind === "text" || isTableEdit) && !e.shiftKey) { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }}
        />
      )}

      {(comments || []).map((c, i) => {
        const sx = c.x * cam.scale + cam.x;
        const sy = c.y * cam.scale + cam.y;
        const active = c.id === activeCommentId;
        return (
          <button key={c.id}
            onClick={() => onOpenComment?.(c.id)}
            title={c.resolved ? "Resolved comment" : "Comment"}
            className="absolute z-20 flex size-7 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full rounded-bl-none border text-xs font-semibold shadow-panel transition-transform hover:scale-110"
            style={{ left: sx, top: sy,
              background: c.resolved ? "var(--color-muted)" : "var(--color-primary)",
              color: c.resolved ? "var(--color-muted-foreground)" : "var(--color-primary-foreground)",
              borderColor: active ? "var(--color-primary)" : "transparent",
              boxShadow: active ? "0 0 0 2px rgba(79,70,229,0.5)" : undefined }}>
            {c.resolved ? <Check size={12} /> : (i + 1)}
          </button>
        );
      })}

      {showStarter && (
        <div className="pointer-events-none flex h-full items-center justify-center">
          <div className="pointer-events-auto w-[460px] rounded-2xl border border-border bg-card/90 p-6 shadow-panel backdrop-blur">
            <h2 className="text-center text-base font-semibold">Start your diagram</h2>
            <p className="mt-1 text-center text-sm text-muted-foreground">Generate with AI, pick a template, import, or drag a shape from the library.</p>
            {/* Generate-with-AI is deferred; the other three are wired: templates
                open the picker, and both import buttons open the auto-detecting importer
                (it handles Excalidraw scenes and Mermaid). */}
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button disabled title="Coming soon" className="flex items-center justify-center gap-2 rounded-lg bg-primary px-3.5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"><Sparkles size={16} /> Generate with AI</button>
              <button onClick={() => onTemplate?.()} title="Start from a ready-made diagram" className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm font-medium hover:border-primary hover:bg-primary-soft"><LayoutTemplate size={16} /> Choose template</button>
              <button onClick={() => onImport?.()} title="Paste a Mermaid flowchart" className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm font-medium hover:border-primary hover:bg-primary-soft"><FileCode2 size={16} /> Import Mermaid</button>
              <button onClick={() => onImport?.()} title="Import an .excalidraw scene" className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm font-medium hover:border-primary hover:bg-primary-soft"><FileJson size={16} /> Excalidraw JSON</button>
            </div>
            <p className="mt-4 text-center text-xs text-muted-foreground">Or press <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">⌘K</kbd> for the command palette</p>
          </div>
        </div>
      )}

      {selBar && (
        <div
          data-testid="sel-toolbar"
          className="absolute z-30 flex -translate-x-1/2 -translate-y-full items-center gap-1 rounded-xl border border-border bg-card px-1.5 py-1 shadow-pop"
          style={{ left: selBar.x, top: Math.max(44, selBar.y - 12) }}
        >
          {["#eef2ff", "#dcfce7", "#fef9c3", "#fee2e2"].map((c) => (
            <button key={c} title={`Fill ${c}`} onClick={() => applyFill(c)} className="size-5 rounded-md border border-border transition-transform hover:scale-110" style={{ background: c }} />
          ))}
          <div className="mx-0.5 h-4 w-px bg-border" />
          <button title="Duplicate  ⌘D" onClick={() => duplicateSelected()} className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"><Copy size={14} /></button>
          <button title="Delete  ⌫" onClick={() => removeSelected()} className="flex size-6 items-center justify-center rounded-md text-destructive hover:bg-destructive/10"><Trash2 size={14} /></button>
        </div>
      )}

      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={() => { setCtxMenu(null); focusCanvas(); }} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
          <div
            data-testid="context-menu" role="menu"
            className="absolute z-50 w-52 overflow-hidden rounded-lg border border-border bg-popover py-1 shadow-pop"
            style={{ left: Math.min(ctxMenu.sx, Math.max(0, size.w - 216)), top: Math.min(ctxMenu.sy, Math.max(0, size.h - 236)) }}
          >
            {ctxMenu.onObject ? (
              <>
                {ctxItem("Duplicate", "⌘D", false, () => runCtx(() => duplicateSelected()))}
                {ctxItem("Bring to front", "⌘]", false, () => runCtx(() => reorderZ("toFront")))}
                {ctxItem("Send to back", "⌘[", false, () => runCtx(() => reorderZ("toBack")))}
                {ctxItem("Lock", "", false, () => runCtx(() => toggleLock()))}
                {ctxItem("Hide", "", false, () => runCtx(() => toggleHide()))}
                {selIds.length >= 2 ? ctxItem("Group", "⌘G", false, () => runCtx(() => groupSelected())) : null}
                <div className="my-1 h-px bg-border" />
                {ctxItem("Delete", "⌫", true, () => runCtx(() => removeSelected()))}
              </>
            ) : (
              <>
                {ctxItem("Select all", "⌘A", false, () => runCtx(() => setSelIds(els.map((x) => x.id))))}
                {ctxItem("Zoom to fit", "", false, () => runCtx(() => fit()))}
              </>
            )}
          </div>
        </>
      )}

      {selTable && (() => {
        const rx = (selTable.x + selTable.w) * cam.scale + cam.x;
        const by = (selTable.y + selTable.h) * cam.scale + cam.y;
        const my = (selTable.y + selTable.h / 2) * cam.scale + cam.y;
        const mx = (selTable.x + selTable.w / 2) * cam.scale + cam.x;
        const btn = "flex size-6 items-center justify-center rounded-md border border-border bg-card text-muted-foreground shadow-panel transition-colors hover:bg-accent hover:text-foreground";
        return (
          <div data-testid="table-controls">
            {/* columns - to the right of the table */}
            <div className="absolute z-30 flex flex-col gap-1" style={{ left: rx + 10, top: my - 27 }}>
              <button data-testid="table-add-col" title="Add column" onClick={() => changeTable(selTable.id, 0, 1)} className={btn}><Plus size={14} /></button>
              <button title="Remove column" onClick={() => changeTable(selTable.id, 0, -1)} className={btn}><Minus size={14} /></button>
            </div>
            {/* rows - below the table */}
            <div className="absolute z-30 flex gap-1" style={{ left: mx - 27, top: by + 10 }}>
              <button data-testid="table-add-row" title="Add row" onClick={() => changeTable(selTable.id, 1, 0)} className={btn}><Plus size={14} /></button>
              <button title="Remove row" onClick={() => changeTable(selTable.id, -1, 0)} className={btn}><Minus size={14} /></button>
            </div>
          </div>
        );
      })()}

      {/* left-16 (not left-3) clears the floating tool rail, which sits at left-3 and is
          vertically centred - on a short viewport its lower end reached the zoom readout
          and clipped the leading digit (100% rendered as 00%). */}
      <div className="absolute bottom-3 left-16 flex items-center gap-0.5 rounded-xl border border-border bg-card p-1 shadow-panel">
        <button title="Zoom out" onClick={() => zoomAroundCenter(1 / 1.2)} className="flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"><Minus size={15} /></button>
        <button title="Reset zoom" onClick={() => setCam((c) => ({ ...c, scale: 1 }))} className="min-w-12 text-center text-xs font-medium tabular-nums hover:text-foreground">{zoomPct}%</button>
        <button title="Zoom in" onClick={() => zoomAroundCenter(1.2)} className="flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"><Plus size={15} /></button>
        <div className="mx-0.5 h-4 w-px bg-border" />
        <button title="Zoom to fit" onClick={fit} className="flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"><Maximize size={15} /></button>
      </div>

      {showMap ? (
        <div className="absolute bottom-3 right-3 overflow-hidden rounded-xl border border-border bg-card shadow-panel">
          <div className="flex items-center justify-between border-b border-border px-2 py-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Map</span>
            <button title="Hide minimap" onClick={() => setShowMap(false)} className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
          </div>
          <MiniMap els={els} cam={cam} size={size} onNav={(wx, wy) => setCam((c) => ({ ...c, x: size.w / 2 - wx * c.scale, y: size.h / 2 - wy * c.scale }))} />
        </div>
      ) : (
        <button title="Show minimap" onClick={() => setShowMap(true)} className="absolute bottom-3 right-3 flex size-9 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground shadow-panel hover:text-foreground"><MapIcon size={16} /></button>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}
